import React, { useState, useEffect, useRef, useMemo } from "react";

// Modules
import { debounce } from "lodash";

// Editor
import EditorJS from "@editorjs/editorjs";
import {
  getEditorConfig,
  getAvailableTools,
} from "../../../../../core/slideHandlers";

// App
import {
  client,
  filesystem_root,
  subdomain,
  proxy,
} from "../../../../../config";
import { patchNode } from "../../../../../core/postNode";
import { default_reveal } from "../../../../../core/defaultReveal";
import Transition from "../../../../../partials/presentations/transition";
import ColorPickerNew from "../../../../../partials/presentations/colorPickerNew";
import BackgroundImagePicker from "../../../../../partials/presentations/backgroundImagePicker";
import LogoPicker from "../../../../../partials/presentations/logoPicker";
import LogoPosition from "../../../../../partials/presentations/logoPosition";
import LayoutPicker from "../../../../../partials/presentations/layoutPicker";
import MediaLibrary from "../../../../../partials/mediaLibrary";
import IconSelector from "../../../../../partials/iconSelector";
import HelpText from "../../help/helpText";
import BespokeMedia from "../../../../../partials/presentations/bespokeMedia";
import {
  jsonParser,
  getThemeJson,
  getLayoutIcons,
  processObject,
  isSafari,
  isValidJson,
  getThemeIds,
  safeUpdateBlock,
} from "../../../../../utils/helpers";
import { backgroundImageURIGenerator } from "../../../../../core/backgroundImageURIGenerator";
import AddAudio from "../../../../../partials/presentations/addAudio";
import CategorySelector from "../../../../../partials/categorySelector";
import HelpTextParser from "../../help/helpTextParser";
import { useSlideContext } from "../../../../../contexts/slideContext";
import { useSlideConfig } from "../../../../../contexts/slideConfigContext";

// Slide options
import HotelCapacityOptions from "./slideSettings/hotelCapacity";
import OverlayDimensions from "./slideSettings/overlayDimensions";
import OverlayOpacity from "./slideSettings/overlayOpacity";
import FragmentOption from "./slideSettings/fragmentOption";

// UI components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import { Loader } from "react-feather";
import {
  Box,
  Dialog,
  DialogContent,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Select,
  MenuItem,
  Alert,
  InputLabel,
  FormGroup,
  FormControl,
  FormControlLabel,
  OutlinedInput,
  Checkbox,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { WarningAmber, KeyboardArrowRight } from "@mui/icons-material";
import { ReactComponent as NounLink } from "../../../../../assets/noun-link.svg";
import Draggable from "react-draggable";
import interact from "interactjs";
import { Paper } from "@mui/material";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

const HELP_TEXT_IMAGE_ROOT = `${filesystem_root}/${subdomain}/themes/presenter_global_theme/dist/images`;

const json_manipulation = (json, theme) => {
  let x = json;

  if (x) {
    x = jsonParser(x);
  } else {
    x = {};
  }

  if (x?.reveal?.slideLayout?.id === "three-usp") {
    let newBlocks = [];

    x.blocks.forEach((block, index) => {
      let newBlock = block;

      if (block.type === "point") {
        newBlock.data.url = `${filesystem_root}/${subdomain}/themes/${theme}/dist/images/point-${
          index + 1
        }.png`;
      }

      newBlocks.push(block);
    });

    x.blocks = newBlocks;
  }

  return x;
};

function PaperComponent(props) {
  return (
    <Draggable
      handle="#ss-modal-title"
      cancel={'[class*="MuiDialogContent-root"], .ss-modal-content *'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

export default function SlideEdit(props) {
  const user = props.user;
  const userAuth = props.userAuth;
  const permissions = props.permissions;

  const { slideConfig } = useSlideConfig();

  const [slideTitle, setSlideTitle] = useState(
    props.slide.title ? props.slide.title : "",
  );
  const [preventDuplicate, setPreventDuplicate] = useState(
    props.slide.prevent_duplicate
      ? parseInt(props.slide.prevent_duplicate)
      : "",
  );
  const editorInstance = useRef();
  const slideRef = useRef();
  const selectorRef = useRef(null);
  const pointerDownRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const DRAG_THRESHOLD = 5;

  const [theme_json, setThemeJson] = useState(
    getThemeJson(props.slide.theme.label),
  );
  let json = useMemo(
    () => json_manipulation(props.slide.slide_json, props.slide.theme.label),
    [props.slide.slide_json, props.slide.theme.label],
  );
  const [editorData, setEditorData] = useState(json ? json : {});
  const [hasImage, setHasImage] = useState(false);
  const [theme, setTheme] = useState({
    label: props.slide.theme.field_label,
    title: props.slide.theme.label,
    value: props.slide.theme.tid,
  });
  const [themeDirectory, setThemeDirectory] = useState(
    `${filesystem_root}/${subdomain}/themes/${props.slide?.theme?.label}`,
  );

  const [showSettingsModal, setSettingsModalShow] = useState(false);
  const [showOverrideModal, setShowOverrideMOdal] = useState(false);

  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showIconSelector, setShowIconSelector] = useState(false);

  const [currentBlock, setCurrentBlock] = useState(null);
  // const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [contentCount, setContentCount] = useState(0);
  const [popupCount, setPopupCount] = useState(0);

  const [mediaType, setMediaType] = useState("all");
  const [slideAudio, setSlideAudio] = useState(false);

  const isLibrarySlide =
    props.slide.initial_presentation === props.presentation.id ? false : true;

  const [isCancelled, setCancelled] = useState(false);
  const [isSaving, setSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isSaved, setSaved] = useState(false);
  const [isError, setError] = useState(false);
  const [jsonError, setJsonError] = useState(false);
  const [remoteJsonError, setRemoteJsonError] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const [transparentSettings, setTransparentSettings] = useState(false);
  const [transparentFocus, setTransparentFocus] = useState("");

  const [slideGrid, setSlideGrid] = useState(false);
  const [contextSlideUpdate, setContextSlideUpdate] = useState(false);

  const [selectedGroupIds, setSelectedGroupIds] = useState(() => new Set());
  const [selectedBlocks, setSelectedBlocks] = useState(() => new Set());
  const [showBlockOutlines, setShowblockOutlines] = useState(false);
  const [focusedGroup, setFocusedGroup] = useState(null);
  const [selectMode, setSelectMode] = useState(false);

  const [blockOrder, setBlockOrder] = useState([]);

  const {
    setActiveSlideTitle,
    setActiveSlideLayout,
    registerToggleGridFunction,
    registerModifySlideTitle,
    setHasSlideAudio,
    registerGroupFunction,
    registerUngroupFunction,
    registerToggleBlockOutlinesFunction,
    setBlockOutlines,
    registerCurrentEditorInstance,
    registerCurrentSlideRef,
    triggerBlockTreeUpdate,
    registerBlockOrderFunction,
    setActiveSlideGrid,
    hoveredNode,
    registerBlockDuplicateFunction,
    setHoveredBlock,
    registerSelectClickHandler,
    setActiveSelectedBlocks,
    setActiveSelectedGroups,
    registerStartSelectModeFunction,
    registerEndSelectModeFunction,
    registerDeleteBlocksFunction,
    setActiveAvailableTools,
    registerAddBlockFunction,
    registerHideBlockFunction,
    registerHideGroupFunction,
  } = useSlideContext();

  const initialRender = useRef(true);

  if (!json?.reveal) {
    json.reveal = default_reveal;
  }

  if (!json.reveal.slideTransition)
    json.reveal.slideTransition = default_reveal.slideTransition;
  if (!json.reveal.slideTransitionSpeed)
    json.reveal.slideTransitionSpeed = default_reveal.slideTransitionSpeed;
  if (!json.reveal.slideLogo) json.reveal.slideLogo = default_reveal.slideLogo;
  if (!json.reveal.slideLogoPosition)
    json.reveal.slideLogoPosition = default_reveal.slideLogoPosition;
  if (!json.reveal.slideLockup)
    json.reveal.slideLockup = default_reveal.slideLockup;
  if (!json.reveal.slideLockupPosition)
    json.reveal.slideLockupPosition = default_reveal.slideLockupPosition;
  if (!json.reveal.slideLayout)
    json.reveal.slideLayout = default_reveal.slideLayout;
  if (!json.reveal.slideContentPosition)
    json.reveal.slideContentPosition = default_reveal.slideContentPosition;
  if (!json.reveal.slideThumbnail)
    json.reveal.slideThumbnail = default_reveal.slideThumbnail;

  if (!json.reveal.slideBackgroundColor)
    json.reveal.slideBackgroundColor = default_reveal.slideBackgroundColor;
  if (!json.reveal.slideBackgroundTransition)
    json.reveal.slideBackgroundTransition =
      default_reveal.slideBackgroundTransition;
  if (!json.reveal.slideBackgroundImage)
    json.reveal.slideBackgroundImage = default_reveal.slideBackgroundImage;
  if (!json.reveal.slideBackgroundOpacity)
    json.reveal.slideBackgroundOpacity = default_reveal.slideBackgroundOpacity;
  if (!json.reveal.slideBackgroundRepeat)
    json.reveal.slideBackgroundRepeat = default_reveal.slideBackgroundRepeat;
  if (!json.reveal.slideBackgroundPosition)
    json.reveal.slideBackgroundPosition =
      default_reveal.slideBackgroundPosition;
  if (!json.reveal.slideBackgroundSize)
    json.reveal.slideBackgroundSize = default_reveal.slideBackgroundSize;

  if (!json.reveal.slideInnerBackgroundColor)
    json.reveal.slideInnerBackgroundColor =
      default_reveal.slideInnerBackgroundColor;
  if (!json.reveal.slideInnerBackgroundImage)
    json.reveal.slideInnerBackgroundImage =
      default_reveal.slideInnerBackgroundImage;
  if (!json.reveal.slideInnerBackgroundOpacity)
    json.reveal.slideInnerBackgroundOpacity =
      default_reveal.slideInnerBackgroundOpacity;
  if (!json.reveal.slideInnerBackgroundRepeat)
    json.reveal.slideInnerBackgroundRepeat =
      default_reveal.slideInnerBackgroundRepeat;
  if (!json.reveal.slideInnerBackgroundPosition)
    json.reveal.slideInnerBackgroundPosition =
      default_reveal.slideInnerBackgroundPosition;
  if (!json.reveal.slideInnerBackgroundSize)
    json.reveal.slideInnerBackgroundSize =
      default_reveal.slideInnerBackgroundSize;
  if (!json.reveal.slideAutoAnimate)
    json.reveal.slideAutoAnimate = default_reveal.slideAutoAnimate;
  if (
    json.reveal?.slideOverlayOpacity === "undefined" ||
    json.reveal?.slideOverlayOpacity === null
  )
    json.reveal.slideOverlayOpacity = default_reveal.slideOverlayOpacity;
  if (!json.reveal.slideOverlayWidth)
    json.reveal.slideOverlayWidth = default_reveal.slideOverlayWidth;
  if (!json.reveal.slideOverlayHeight)
    json.reveal.slideOverlayHeight = default_reveal.slideOverlayHeight;
  if (!json.reveal.slideContentWidth)
    json.reveal.slideContentWidth = default_reveal.slideContentWidth;
  if (!json.reveal.slideOverlayColor)
    json.reveal.slideOverlayColor = default_reveal.slideOverlayColor;
  if (!json.reveal.slideOverlayRGB)
    json.reveal.slideOverlayRGB = default_reveal.slideOverlayRGB;
  if (!json.reveal.slideChapterMarker)
    json.reveal.slideChapterMarker = default_reveal.slideChapterMarker;
  if (!json.reveal.slideDataTableColumn)
    json.reveal.slideDataTableColumn = default_reveal.slideDataTableColumn;
  if (!json.reveal.archContentAlignment)
    json.reveal.archContentAlignment = default_reveal.archContentAlignment;
  if (!json.reveal.capacityTags)
    json.reveal.capacityTags = default_reveal.capacityTags;
  if (!json.reveal.slidePattern)
    json.reveal.slidePattern = default_reveal.slidePattern;
  if (!json.reveal.fragmentCount)
    json.reveal.fragmentCount = default_reveal.fragmentCount;

  const [slideTransition, setSlideTransition] = useState(
    json.reveal.slideTransition,
  );
  const [slideTransitionSpeed, setSlideTransitionSpeed] = useState(
    json.reveal.slideTransitionSpeed,
  );
  const [slideLogo, setSlideLogo] = useState(json.reveal.slideLogo);
  const [slideLogoPosition, setSlideLogoPosition] = useState(
    json.reveal.slideLogoPosition,
  );
  const [slideLockup, setSlideLockup] = useState(json.reveal.slideLockup);
  const [slideLockupPosition, setSlideLockupPosition] = useState(
    json.reveal.slideLockupPosition,
  );
  const [slideLayout, setSlideLayout] = useState(json.reveal.slideLayout);
  const [slideContentPosition, setSlideContentPosition] = useState(
    json.reveal.slideContentPosition,
  );
  const [slideAudioData, setSlideAudioData] = useState(props.slideAudioData);
  const [slideThumbnail, setSlideThumbnail] = useState(
    json.reveal.slideThumbnail,
  );
  const [slideFiles, setSlideFiles] = useState();

  const [slideBackgroundColor, setSlideBackgroundColor] = useState(
    json.reveal.slideBackgroundColor,
  );
  const [slideBackgroundTransition, setSlideBackgroundTransition] = useState(
    json.reveal.slideBackgroundTransition,
  );
  const [slideBackgroundImage, setSlideBackgroundImage] = useState(
    json.reveal.slideBackgroundImage,
  );
  const [slidePattern, setSlidePattern] = useState(json.reveal.slidePattern);
  const [slideBackgroundOpacity, setSlideBackgroundOpacity] = useState(
    json.reveal.slideBackgroundOpacity,
  );
  const [slideBackgroundRepeat, setSlideBackgroundRepeat] = useState(
    json.reveal.slideBackgroundRepeat,
  );
  const [slideBackgroundPosition, setSlideBackgroundPosition] = useState(
    json.reveal.slideBackgroundPosition,
  );
  const [slideBackgroundSize, setSlideBackgroundSize] = useState(
    json.reveal.slideBackgroundSize,
  );

  const [slideInnerBackgroundColor, setSlideInnerBackgroundColor] = useState(
    json.reveal.slideInnerBackgroundColor,
  );
  const [slideInnerBackgroundImage, setSlideInnerBackgroundImage] = useState(
    json.reveal.slideInnerBackgroundImage,
  );
  const [slideInnerBackgroundOpacity, setSlideInnerBackgroundOpacity] =
    useState(json.reveal.slideInnerBackgroundOpacity);
  const [slideInnerBackgroundRepeat, setSlideInnerBackgroundRepeat] = useState(
    json.reveal.slideInnerBackgroundRepeat,
  );
  const [slideInnerBackgroundPosition, setSlideInnerBackgroundPosition] =
    useState(json.reveal.slideInnerBackgroundPosition);
  const [slideInnerBackgroundSize, setSlideInnerBackgroundSize] = useState(
    json.reveal.slideInnerBackgroundSize,
  );
  const [slideAutoAnimate, setSlideAutoAnimate] = useState(
    json.reveal.slideAutoAnimate,
  );
  const [slideOverlayOpacity, setSlideOverlayOpacity] = useState(
    json.reveal.slideOverlayOpacity,
  );
  const [slideOverlayWidth, setSlideOverlayWidth] = useState(
    json.reveal.slideOverlayWidth,
  );
  const [slideOverlayHeight, setSlideOverlayHeight] = useState(
    json.reveal.slideOverlayHeight,
  );
  const [slideContentWidth, setSlideContentWidth] = useState(
    json.reveal.slideContentWidth,
  );
  const [slideOverlayColor, setSlideOverlayColor] = useState(
    json.reveal.slideOverlayColor,
  );
  const [slideOverlayRGB, setSlideOverlayRGB] = useState(
    json.reveal.slideOverlayRGB,
  );
  const [slideChapterMarker, setSlideChapterMarker] = useState(
    json.reveal.slideChapterMarker ? json.reveal.slideChapterMarker : "",
  );
  const [slideDataTableColumn, setSlideDataTableColumn] = useState(
    json.reveal.slideDataTableColumn,
  );
  const [archContentAlignment, setArchContentAlignment] = useState(
    json.reveal.archContentAlignment,
  );
  const [capacityTags, setCapacityTags] = useState(json.reveal.capacityTags);
  const [fragmentCount, setFragmentCount] = useState(json.reveal.fragmentCount);

  const [slideNotes, setSlideNotes] = useState(props.slideNotes ?? "");

  // const [helpText, setHelpText] = useState('');
  const helpText = "";

  // Used on Cancel changes
  const [originalData, setOriginalData] = useState({
    originalTheme: {
      label: props.slide.theme.field_label,
      title: props.slide.theme.label,
      value: props.slide.theme.tid,
    },
    originalSlideTransition: json.reveal.slideTransition,
    originalSlideTransitionSpeed: json.reveal.slideTransitionSpeed,
    originalSlideLogo: json.reveal.slideLogo,
    originalSlideLogoPosition: json.reveal.slideLogoPosition,
    originalSlideLockup: json.reveal.slideLockup,
    originalSlideLockupPosition: json.reveal.slideLockupPosition,
    originalSlideLayout: json.reveal.slideLayout,
    originalSlideContentPosition: json.reveal.slideContentPosition,
    originalSlideThumbnail: json.reveal.slideThumbnail,
    originalSlideBackgroundColor: json.reveal.slideBackgroundColor,
    originalSlideBackgroundTransition: json.reveal.slideBackgroundTransition,
    originalSlideBackgroundImage: json.reveal.slideBackgroundImage,
    originalSlidePattern: json.reveal.slidePattern,
    originalSlideBackgroundOpacity: json.reveal.slideBackgroundOpacity,
    originalSlideBackgroundRepeat: json.reveal.slideBackgroundRepeat,
    originalSlideBackgroundPosition: json.reveal.slideBackgroundPosition,
    originalSlideBackgroundSize: json.reveal.slideBackgroundSize,
    originalSlideInnerBackgroundColor: json.reveal.slideInnerBackgroundColor,
    originalSlideInnerBackgroundImage: json.reveal.slideInnerBackgroundImage,
    originalSlideInnerBackgroundOpacity:
      json.reveal.slideInnerBackgroundOpacity,
    originalSlideInnerBackgroundRepeat: json.reveal.slideInnerBackgroundRepeat,
    originalSlideInnerBackgroundPosition:
      json.reveal.slideInnerBackgroundPosition,
    originalSlideInnerBackgroundSize: json.reveal.slideInnerBackgroundSize,
    originalSlideAutoAnimate: json.reveal.slideAutoAnimate,
    originalSlideOverlayOpacity: json.reveal.slideOverlayOpacity,
    originalSlideOverlayWidth: json.reveal.slideOverlayWidth,
    originalSlideOverlayHeight: json.reveal.slideOverlayHeight,
    originalSlideContentWidth: json.reveal.slideContentWidth,
    originalSlideOverlayColor: json.reveal.slideOverlayColor,
    originalSlideOverlayRGB: json.reveal.slideOverlayRGB,
    originalSlideChapterMarker: json.reveal.slideChapterMarker,
    originalSlideDataTableColumn: json.reveal.slideDataTableColumn,
    originalArchCOntentAlignment: json.reveal.archContentAlignment,
    originalCapacityTags: json.reveal.capacityTags,
    originalSlideAudioData: props.slideAudioData,
    originalSlideNotes: props.slideNotes,
    originalFragmentCount: json.reveal.fragmentCount,
  });

  // init editor
  useEffect(() => {
    if (!editorInstance.current && slideLayout.id !== "advanced") {
      initEditor();

      const isRemoteJsonValid = isValidJson(props.slide.slide_json);
      if (!isRemoteJsonValid) {
        setRemoteJsonError(true);
      }
    }
    return () => {
      if (editorInstance && editorInstance.current) {
        editorInstance.current.destroy();
        editorInstance.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revert to original state of slide settings if it isn't saved.
  useEffect(() => {
    if (
      originalData.originalTheme === theme ||
      originalData.originalSlideTransition === slideTransition ||
      originalData.originalSlideTransitionSpeed === slideTransitionSpeed ||
      originalData.originalSlideLogo === slideLogo ||
      originalData.originalSlideLogoPosition === slideLogoPosition ||
      originalData.originalSlideLockup === slideLockup ||
      originalData.originalSlideLockupPosition === slideLockupPosition ||
      originalData.originalSlideLayout === slideLayout ||
      originalData.originalSlideContentPosition === slideContentPosition ||
      originalData.originalSlideAudioData === slideAudioData ||
      originalData.originalSlideThumbnail === slideThumbnail ||
      originalData.originalSlideBackgroundColor === slideBackgroundColor ||
      originalData.originalSlideBackgroundTransition ===
        slideBackgroundTransition ||
      originalData.originalSlideBackgroundImage === slideBackgroundImage ||
      originalData.originalSlidePattern === slidePattern ||
      originalData.originalSlideBackgroundOpacity === slideBackgroundOpacity ||
      originalData.originalSlideBackgroundRepeat === slideBackgroundRepeat ||
      originalData.originalSlideBackgroundPosition ===
        slideBackgroundPosition ||
      originalData.originalSlideBackgroundSize === slideBackgroundSize ||
      originalData.originalSlideInnerBackgroundColor ===
        slideInnerBackgroundColor ||
      originalData.originalSlideInnerBackgroundImage ===
        slideInnerBackgroundImage ||
      originalData.originalSlideInnerBackgroundOpacity ===
        slideInnerBackgroundOpacity ||
      originalData.originalSlideInnerBackgroundRepeat ===
        slideInnerBackgroundRepeat ||
      originalData.originalSlideInnerBackgroundPosition ===
        slideInnerBackgroundPosition ||
      originalData.originalSlideInnerBackgroundSize ===
        slideInnerBackgroundSize ||
      originalData.originalSlideAutoAnimate === slideAutoAnimate ||
      originalData.originalSlideOverlayOpacity === slideOverlayOpacity ||
      originalData.originalSlideOverlayWidth === slideOverlayWidth ||
      originalData.originalSlideOverlayHeight === slideOverlayHeight ||
      originalData.originalSlideContentWidth === slideContentWidth ||
      originalData.originalSlideOverlayColor === slideOverlayColor ||
      originalData.originalSlideOverlayRGB === slideOverlayRGB ||
      originalData.originalSlideChapterMarker === slideChapterMarker ||
      originalData.originalSlideDataTableColumn === slideDataTableColumn ||
      originalData.originalArchCOntentAlignment === archContentAlignment ||
      originalData.originalCapacityTags === capacityTags ||
      originalData.originalSlideNotes === slideNotes ||
      originalData.originalFragmentCount === fragmentCount
    ) {
      if (isCancelled) {
        setCancelled(false);
        props.onRevealUpdate();
      }
    }
  }, [
    originalData,
    theme,
    slideBackgroundColor,
    slideTransition,
    slideTransitionSpeed,
    slideBackgroundTransition,
    props,
    isCancelled,
    slideBackgroundImage,
    slidePattern,
    slideBackgroundOpacity,
    slideBackgroundRepeat,
    slideBackgroundPosition,
    slideBackgroundSize,
    slideLogo,
    slideLogoPosition,
    slideLockup,
    slideLockupPosition,
    slideLayout,
    slideInnerBackgroundColor,
    slideInnerBackgroundImage,
    slideInnerBackgroundOpacity,
    slideInnerBackgroundRepeat,
    slideInnerBackgroundPosition,
    slideInnerBackgroundSize,
    slideAudioData,
    slideContentPosition,
    slideAutoAnimate,
    slideOverlayOpacity,
    slideOverlayWidth,
    slideOverlayHeight,
    slideContentWidth,
    slideOverlayColor,
    slideOverlayRGB,
    slideChapterMarker,
    slideThumbnail,
    slideDataTableColumn,
    archContentAlignment,
    capacityTags,
    slideNotes,
    fragmentCount,
  ]);

  // Update slide json by postMessage
  // Needed for out of ordinary save/update
  // ie from toolbar/ai
  useEffect(() => {
    const handler = async (event) => {
      // SECURITY CHECK — do not skip
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "updateSlide") {
        if (event.data.payload) {
          if (event.data.payload.id === props.slide.id) {
            await editorInstance.current.render(event.data.payload.json);
          }
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show logo settings
  useEffect(() => {
    if (props.showLogoSettings) {
      setSettingsModalShow(true);
    }
  }, [props.showLogoSettings]);

  // Show background settings
  useEffect(() => {
    if (props.showBackgroundSettings) {
      setSettingsModalShow(true);
    }
  }, [props.showBackgroundSettings]);

  // Show transition settings
  useEffect(() => {
    if (props.showTransitionSettings) {
      setSettingsModalShow(true);
    }
  }, [props.showTransitionSettings]);

  // Show slide settings
  useEffect(() => {
    if (props.showSlideSettings) {
      setSettingsModalShow(true);
    }
  }, [props.showSlideSettings]);

  // Show settings modal on help dialog
  useEffect(() => {
    if (props.showHelpDialog) {
      setSettingsModalShow(true);
    }
  }, [props.showHelpDialog]);

  // Show settings modal on slide nodes
  useEffect(() => {
    if (props.showSlideNotes) {
      setShowOverrideMOdal(true);
    }
  }, [props.showSlideNotes]);

  // Show settings modal on slide audio
  useEffect(() => {
    if (props.showSlideAudioSettings) {
      setShowOverrideMOdal(true);
    }
  }, [props.showSlideAudioSettings]);

  // Show audio settings
  useEffect(() => {
    setMediaType("audio");
    setShowMediaLibrary(slideAudio);
  }, [slideAudio]);

  // Update slide audio
  useEffect(() => {
    if (props.activeTrigger) {
      setHasSlideAudio(slideAudioData ? true : false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, props.activeTrigger, slideAudioData]);

  // useEffects to handle slideTitle updates through slide context --START
  useEffect(() => {
    if (props.activeTrigger) {
      setActiveSlideLayout(slideLayout.id);
      if (slideLayout.id === "custom") {
        registerToggleGridFunction(() => {
          setSlideGrid((prevSlideGrid) => !prevSlideGrid);
        });
        registerStartSelectModeFunction(() => {
          startSelectMode();
        });
        registerEndSelectModeFunction(() => {
          endSelectMode();
        });
        registerGroupFunction(() => groupSelectedBlocks());
        registerUngroupFunction(() => ungroupSelectedBlocks());
        registerBlockDuplicateFunction(() => duplicateBlockQueue());
        registerDeleteBlocksFunction(() => deleteSelectedBlocks());
        registerSelectClickHandler((selectionType, elementId) =>
          customClickHandler(selectionType, elementId),
        );
        registerAddBlockFunction((blockType) => addBlock(blockType));
        registerHideBlockFunction((blockId) => hideBlock(blockId));
        registerHideGroupFunction((groupId) => hideGroup(groupId));
      }
      if (slideRef.current) {
        registerToggleBlockOutlinesFunction(() => {
          setShowblockOutlines((prev) => {
            const next = !prev;
            setBlockOutlines(next);
            return next;
          });
        });
        setBlockOutlines(showBlockOutlines);
      }
    }
    // eslint-disable-next-line
  }, [
    props.activeTrigger,
    registerToggleGridFunction,
    registerGroupFunction,
    registerUngroupFunction,
  ]);

  useEffect(() => {
    if (props.activeTrigger) {
      let tempSlideTitle = slideTitle;
      setActiveSlideTitle(tempSlideTitle);
      registerModifySlideTitle((value) => {
        setActiveSlideTitle(value);
        setSlideTitle(value);
        debouncedSlideContextUpdate();
      });
    }
    // eslint-disable-next-line
  }, [props.activeTrigger]);

  useEffect(() => {
    if (props.activeTrigger && !isLibrarySlide && slideLayout.id === "custom") {
      if (editorInstance.current) {
        registerCurrentEditorInstance(editorInstance.current);
      }
      if (slideRef.current) {
        registerCurrentSlideRef(slideRef.current);
      }
      registerBlockOrderFunction((arr) => {
        setBlockOrder(arr);
      });
    } else if (
      props.activeTrigger &&
      (isLibrarySlide || slideLayout.id === "advanced")
    ) {
      registerCurrentEditorInstance(null);
      registerCurrentSlideRef(null);
      registerBlockOrderFunction(null);
    }
    if (props.activeTrigger && slideLayout.id === "custom" && !isLibrarySlide) {
      setActiveAvailableTools(getAvailableTools(slideLayout, slideConfig));
    } else if (props.activeTrigger) {
      setActiveAvailableTools(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.activeTrigger,
    isLibrarySlide,
    slideLayout.id,
    slideConfig,
    isLibrarySlide,
  ]);

  useEffect(() => {
    const clearHovered = () => {
      const targets = slideRef.current.querySelectorAll(".block-tree-hovered");
      if (targets?.length > 0) {
        targets.forEach((e) => e.classList.remove("block-tree-hovered"));
      }
    };
    if (slideRef.current) {
      if (hoveredNode !== null) {
        clearHovered();
        const targets = slideRef.current.querySelectorAll(
          hoveredNode.type === "group"
            ? `[data-group="${hoveredNode.id}"], 
            [data-group-frame-id="frame-${hoveredNode.id}"]`
            : `[data-id="${hoveredNode.id}"]`,
        );
        if (targets?.length > 0) {
          targets.forEach((e) => e.classList.add("block-tree-hovered"));
        }
      } else {
        clearHovered();
      }
    }
  }, [props.activeTrigger, hoveredNode]);

  useEffect(() => {
    if (editorInstance.current && blockOrder && blockOrder?.length > 0) {
      reorderEditorBlocksById(editorInstance.current, blockOrder);
    }
  }, [blockOrder]);

  useEffect(() => {
    if (props.activeTrigger && slideLayout.id === "custom" && !isLibrarySlide) {
      setActiveSelectedBlocks(selectedBlocks);
      setActiveSelectedGroups(selectedGroupIds);
    } else {
      setActiveSelectedBlocks(new Set());
      setActiveSelectedGroups(new Set());
    }
    // eslint-disable-next-line
  }, [props.activeTrigger, selectedBlocks, selectedGroupIds]);

  useEffect(() => {
    const slide = slideRef.current;
    if (!slide) return;

    const blocks = slide.querySelectorAll(".ce-block");
    blocks.forEach((block) => {
      const id = block.dataset.id;
      if (selectedBlocks.has(id)) {
        block.classList.add("selected");
      } else {
        block.classList.remove("selected");
      }
    });

    if (selectedGroupIds.size > 0) {
      const groupIds = Array.from(selectedGroupIds);
      groupIds.forEach((groupId) => {
        const selectedBlocksInGroup = slide.querySelectorAll(
          `.ce-block.selected[data-group="${groupId}"]`,
        );
        if (selectedBlocksInGroup.length < 1) {
          setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            next.delete(groupId);
            return next;
          });
        }
      });
    }

    const controller = new AbortController();

    if (selectedBlocks.size > 0) {
      handleSelector();

      const selectorFrame = selectorRef.current;
      if (selectorFrame) {
        selectorFrame.addEventListener(
          "pointerdown",
          (e) => {
            if (!e.shiftKey) return;
            const hits = document.elementsFromPoint(e.clientX, e.clientY);
            const foundBlock = hits.find((el) => el.closest(".ce-block"));
            if (!foundBlock) return;
            e.stopImmediatePropagation();
            e.preventDefault();
            const block = foundBlock.closest(".ce-block");
            toggleBlock(block.dataset.id);
          },
          { signal: controller.signal },
        );
        if (slideRef.current && editorInstance.current) {
          interact(selectorFrame)
            .draggable({
              listeners: {
                start(event) {
                  interactStart([selectorFrame], slideRef.current);
                },
                move(event) {
                  const blocksToMove = Array.from(
                    slideRef.current.querySelectorAll(".ce-block.selected"),
                  );
                  blocksToMove.push(event.target);

                  interactMove(blocksToMove, event);
                },
                end(event) {
                  const blocksToMove = Array.from(
                    slideRef.current.querySelectorAll(".ce-block.selected"),
                  );
                  const blocksApi = editorInstance.current.blocks;
                  interactEnd(blocksToMove, blocksApi);
                },
              },
            })
            .on("down", (event) => {
              event.preventDefault();
            });
        }
      }
    } else {
      if (selectorRef.current) {
        selectorRef.current.remove();
        selectorRef.current = null;
      }
    }

    return () => {
      controller.abort();
    };
  }, [selectedBlocks]);

  useEffect(() => {
    const frames = slideRef.current.querySelectorAll(".group-frame");

    frames.forEach((frame) => {
      const groupId = frame.dataset.groupFrameId?.replace("frame-", "");
      const isSelected = selectedGroupIds.has(groupId);
      if (isSelected) {
        frame.classList.add("hidden");
      } else {
        frame.classList.remove("hidden");
      }
    });
  }, [selectedGroupIds]);

  useEffect(() => {
    if (!slideRef.current || focusedGroup === null) return;

    const groupFrame = slideRef.current.querySelector(
      `.group-frame[data-group-frame-id="frame-${focusedGroup}"]`,
    );

    const groupedBlocks = slideRef.current.querySelectorAll(
      `.ce-block[data-group="${focusedGroup}"]`,
    );

    groupFrame?.classList.add("group-focused");

    groupedBlocks.forEach((block) => {
      block.classList.add("focused");
    });

    const exitFocus = (e) => {
      // If clicking inside the group, keep focus
      if (
        groupFrame?.contains(e.target) ||
        [...groupedBlocks].some((b) => b.contains(e.target))
      ) {
        return;
      }

      // remove classes
      groupFrame?.classList.remove("group-focused");
      groupedBlocks.forEach((block) => {
        block.classList.remove("focused");
      });

      // clear state
      setFocusedGroup(null);

      // remove listener immediately
      window.removeEventListener("click", exitFocus);
    };

    window.addEventListener("click", exitFocus);
    return () => {
      window.removeEventListener("click", exitFocus);
    };
  }, [focusedGroup]);

  useEffect(() => {
    if (props.activeTrigger) {
      setActiveSlideGrid(slideGrid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeTrigger, slideGrid]);

  useEffect(() => {
    if (contextSlideUpdate) {
      updateSlide();
    }
    // eslint-disable-next-line
  }, [contextSlideUpdate]);
  // useEffects to handle slideTitle updates through slide context --END

  // Save current block index when launching media library
  // useEffect(() => {
  //   if (editorInstance.current) {
  //     setCurrentBlockIndex(
  //       editorInstance.current.blocks.getCurrentBlockIndex()
  //     );
  //   }
  // }, [showMediaLibrary]);

  // Callback to parent to hide toolbar
  useEffect(() => {
    if (jsonError || remoteJsonError) {
      props.onCorruptJson(props.slide.id);
    }
  }, [jsonError, props, remoteJsonError]);

  let overlayContent = document.querySelector(
    `#slide-${props.slide.id}-editor .codex-editor`,
  );

  // vertical overlay style changes
  useEffect(() => {
    if (slideLayout.id === "image-with-vertical-overlay" && overlayContent) {
      overlayContent.style.width = `${slideOverlayWidth}%`;
    }
    if (slideLayout.id === "image-with-horizontal-overlay" && overlayContent) {
      overlayContent.closest(".inner-container").style.height =
        slideOverlayHeight > 15 ? `${slideOverlayHeight}%` : "auto";
    }
  }, [slideOverlayWidth, slideOverlayHeight, overlayContent, slideLayout.id]);

  const reorderEditorBlocksById = (editor, targetOrder) => {
    if (!editor?.blocks || !Array.isArray(targetOrder)) return;

    const blocksAPI = editor.blocks;

    const getCurrentOrder = () => {
      const count = blocksAPI.getBlocksCount();
      const ids = [];

      for (let i = 0; i < count; i++) {
        const block = blocksAPI.getBlockByIndex(i);
        if (block?.id) ids.push(block.id);
      }

      return ids;
    };

    for (let targetIndex = 0; targetIndex < targetOrder.length; targetIndex++) {
      const desiredId = targetOrder[targetIndex];

      const currentOrder = getCurrentOrder();
      const currentIndex = currentOrder.indexOf(desiredId);

      if (currentIndex === -1 || currentIndex === targetIndex) {
        continue;
      }

      blocksAPI.move(targetIndex, currentIndex);
    }

    setBlockOrder([]);
  };

  // Initialize editor.js
  const initEditor = async () => {
    if (editorInstance.current) {
      editorInstance.current.destroy();
      editorInstance.current = null;
    }

    const layoutConfig = slideConfig[slideLayout.id] ?? null;
    const minHeight = layoutConfig?.minHeight ?? 0;

    const ctx = {
      setCurrentBlock,
      setShowMediaLibrary,
      setSlideFiles,
      setShowIconSelector,
      setMediaType,
      themeLabel: props.slide.theme.label,
      themeDirectory,
      presentationId: props.presentation.id,
      slideId: props.slide.id,
      googleApiKey: theme_json?.googleApiKey,
      pin: theme_json?.icons?.pin,
      editorInstance: null,
    };

    const { tools, default_block } = getEditorConfig(
      slideLayout,
      client,
      ctx,
      theme_json,
      themeDirectory,
      slideConfig,
    );

    if (editorData.blocks && editorData.blocks.length > 0) {
      editorData.blocks.map((obj) => processObject({ ...obj }));
    }

    const isReadOnly = isLibrarySlide === true ? true : false;

    const editor = new EditorJS({
      /**
       * Id of Element that should contain the Editor
       */
      readOnly: isReadOnly,
      logLevel: "ERROR",
      holder: `slide-${props.slide.id}-editor`,
      inlineToolbar: true,
      minHeight: minHeight,
      tools: tools,
      defaultBlock: default_block,

      /**
       * Previously saved data that should be rendered
       */
      data: editorData,
      onReady: () => {
        editorInstance.current = editor;
        ctx.editorInstance = editor;
        // set loading false

        const blockLength = editor.blocks.getBlocksCount();
        let isBlockImage = false;
        let imageIndex = 0;
        let contentIndex = 0;
        let popupIndex = 0;
        let imgTxtOverlayIndex = 0;
        let captionedImageIndex = 0;

        for (var i = 0; i < blockLength; i++) {
          const block = editor.blocks.getBlockByIndex(i);
          if (block && block.holder && block.name) {
            const element = block?.holder;
            const blockType = block?.name;

            element?.classList.add(blockType);

            if (blockType === "image" || blockType === "inlineImage") {
              imageIndex++;
              element?.setAttribute("data-block", imageIndex);
              isBlockImage = true;
            }

            if (blockType === "body" || blockType === "list") {
              contentIndex++;
              element?.setAttribute("data-block", contentIndex);
            }

            if (blockType === "popup") {
              popupIndex++;
              element?.setAttribute("data-block", popupIndex);
            }

            if (blockType === "imageTextOverlay") {
              imgTxtOverlayIndex++;
              element?.setAttribute("data-block", imgTxtOverlayIndex);
            }

            if (blockType === "captionedImage") {
              captionedImageIndex++;
              element?.setAttribute("data-block", captionedImageIndex);
            }
          }
        }

        setPopupCount(popupIndex);
        setContentCount(contentIndex);
        setHasImage(isBlockImage);
        if (props.activeTrigger && !isLibrarySlide) {
          registerCurrentEditorInstance(editorInstance.current);
        } else if (props.activeTrigger && isLibrarySlide) {
          registerCurrentEditorInstance(null);
        }
        processBlockGroups();
      },
      onChange: async (api, event) => {
        if (!api.readOnly?.isEnabled) {
          const editor = editorInstance.current;
          const blockLength = editor?.blocks?.getBlocksCount();
          let isBlockImage = false;
          let imageIndex = 0;
          let contentIndex = 0;
          let popupIndex = 0;
          let imgTxtOverlayIndex = 0;
          let captionedImageIndex = 0;

          for (var i = 0; i < blockLength; i++) {
            const block = editor.blocks.getBlockByIndex(i);
            if (block && block.holder && block.name) {
              const element = block?.holder;
              const blockType = block?.name;

              element?.classList.add(blockType);

              if (blockType === "image" || blockType === "inlineImage") {
                imageIndex++;
                element?.setAttribute("data-block", imageIndex);
                isBlockImage = true;
              }

              if (blockType === "body" || blockType === "list") {
                contentIndex++;
                element?.setAttribute("data-block", contentIndex);
              }

              if (blockType === "popup") {
                popupIndex++;
                element?.setAttribute("data-block", popupIndex);
              }

              if (blockType === "imageTextOverlay") {
                imgTxtOverlayIndex++;
                element?.setAttribute("data-block", imgTxtOverlayIndex);
              }

              if (blockType === "captionedImage") {
                captionedImageIndex++;
                element?.setAttribute("data-block", captionedImageIndex);
              }
            }
          }

          setPopupCount(popupIndex);
          setContentCount(contentIndex);
          setHasImage(isBlockImage);
          let content = await editor.save();
          setEditorData(content);
          props.onRevealUpdate(); // so animations are recorded straight away
          triggerBlockTreeUpdate();
          processBlockGroups();
        }
      },
    });
  };

  // On editor change, update slide
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      debounceUpdate();
    }
    // eslint-disable-next-line
  }, [editorData]);

  /**
   * Auto save
   *
   * Updates slide
   */
  const updateSlide = async () => {
    let newData = editorData;

    if (
      editorInstance &&
      editorInstance.current &&
      !editorInstance.current.readOnly?.isEnabled
    ) {
      newData = await editorInstance.current.save();
    }

    if (newData === "") {
      newData = {
        blocks: [],
        reveal: {},
      };
    }

    newData.reveal = {
      slideTransition: slideTransition,
      slideTransitionSpeed: slideTransitionSpeed,
      slideLogo: slideLogo,
      slideLogoPosition: slideLogoPosition,
      slideLockup: slideLockup,
      slideLockupPosition: slideLockupPosition,
      slideLayout: slideLayout,
      slideContentPosition: slideContentPosition,
      slideThumbnail: slideThumbnail,
      slideBackgroundColor: slideBackgroundColor,
      slideBackgroundTransition: slideBackgroundTransition,
      slideBackgroundImage: slideBackgroundImage,
      slidePattern: slidePattern,
      slideBackgroundOpacity: slideBackgroundOpacity,
      slideBackgroundRepeat: slideBackgroundRepeat,
      slideBackgroundPosition: slideBackgroundPosition,
      slideBackgroundSize: slideBackgroundSize,
      slideInnerBackgroundColor: slideInnerBackgroundColor,
      slideInnerBackgroundImage: slideInnerBackgroundImage,
      slideInnerBackgroundOpacity: slideInnerBackgroundOpacity,
      slideInnerBackgroundRepeat: slideInnerBackgroundRepeat,
      slideInnerBackgroundPosition: slideInnerBackgroundPosition,
      slideInnerBackgroundSize: slideInnerBackgroundSize,
      slideAutoAnimate: slideAutoAnimate,
      slideOverlayOpacity: slideOverlayOpacity,
      slideOverlayWidth: slideOverlayWidth,
      slideOverlayHeight: slideOverlayHeight,
      slideContentWidth: slideContentWidth,
      slideOverlayColor: slideOverlayColor,
      slideOverlayRGB: slideOverlayRGB,
      slideChapterMarker: slideChapterMarker,
      slideDataTableColumn: slideDataTableColumn,
      archContentAlignment: archContentAlignment,
      capacityTags: capacityTags,
      fragmentCount: fragmentCount,
    };

    let slideData = {
      id: props.slide.id,
      json: newData,
      node_data: {
        title: slideTitle,
      },
      presentation_id: props.presentation.id,
      prevent_duplicate: preventDuplicate ? 1 : 0,
      theme: theme.value,
      should: slideLayout.id,
    };

    if (
      newData.reveal.slideLayout.id === "custom" &&
      originalData.originalSlideLayout.id !== newData.reveal.slideLayout.id
    ) {
      slideData.prevent_duplicate = 1;
    }

    const isValid = isValidJson(JSON.stringify(slideData.json));

    if (isValid) {
      setSaving(true);
      setSaved(false);
      setJsonError(false);
      setError(false);

      patchNode("api/slide/" + props.slide.id, slideData, props.csrf_token)
        .then((_response) => {
          // if (shouldSaveOldJson) {
          //   setPrevEditorData(newData);
          // }

          // shouldSaveOldJson.current = false;

          setOriginalData({
            originalTheme: theme,
            originalSlideTransition: newData.reveal.slideTransition,
            originalSlideTransitionSpeed: newData.reveal.slideTransitionSpeed,
            originalSlideLogo: newData.reveal.slideLogo,
            originalSlideLogoPosition: newData.reveal.slideLogoPosition,
            originalSlideLockup: newData.reveal.slideLockup,
            originalSlideLockupPosition: newData.reveal.slideLockupPosition,
            originalSlideLayout: newData.reveal.slideLayout,
            originalSlideContentPosition: newData.reveal.slideContentPosition,
            originalSlideAudioData: newData.reveal.slideAudioData,
            originalSlideThumbnail: newData.reveal.slideThumbnail,
            originalSlideBackgroundColor: newData.reveal.slideBackgroundColor,
            originalSlideBackgroundTransition:
              newData.reveal.slideBackgroundTransition,
            originalSlideBackgroundImage: newData.reveal.slideBackgroundImage,
            originalSlidePattern: newData.reveal.slidePattern,
            originalSlideBackgroundOpacity:
              newData.reveal.slideBackgroundOpacity,
            originalSlideBackgroundRepeat: newData.reveal.slideBackgroundRepeat,
            originalSlideBackgroundPosition:
              newData.reveal.slideBackgroundPosition,
            originalSlideBackgroundSize: newData.reveal.slideBackgroundSize,
            originalSlideInnerBackgroundColor:
              newData.reveal.slideInnerBackgroundColor,
            originalSlideInnerBackgroundImage:
              newData.reveal.slideInnerBackgroundImage,
            originalSlideInnerBackgroundOpacity:
              newData.reveal.slideInnerBackgroundOpacity,
            originalSlideInnerBackgroundRepeat:
              newData.reveal.slideInnerBackgroundRepeat,
            originalSlideInnerBackgroundPosition:
              newData.reveal.slideInnerBackgroundPosition,
            originalSlideInnerBackgroundSize:
              newData.reveal.slideInnerBackgroundSize,
            originalSlideAutoAnimate: newData.reveal.slideAutoAnimate,
            originalSlideOverlayOpacity: newData.reveal.slideOverlayOpacity,
            originalSlideOverlayWidth: newData.reveal.slideOverlayWidth,
            originalSlideOverlayHeight: newData.reveal.slideOverlayHeight,
            originalSlideContentWidth: newData.reveal.slideContentWidth,
            originalSlideOverlayColor: newData.reveal.slideOverlayColor,
            originalSlideOverlayRGB: newData.reveal.slideOverlayRGB,
            originalSlideChapterMarker: newData.reveal.slideChapterMarker,
            originalSlideDataTableColumn: newData.reveal.slideDataTableColumn,
            originalArchCOntentAlignment: newData.reveal.archContentAlignment,
            originalCapacityTags: newData.reveal.capacityTags,
            originalSlideNotes: newData.reveal.slideNotes,
            originalFragmentCount: newData.reveal.fragmentCount,
          });

          setSaving(false);
          setSaved(true);
          setError(false);
          setContextSlideUpdate(false);
          // props.onSlideUpdate();
        })
        .catch((error) => {
          setSaving(false);
          setSaved(false);
          setJsonError(false);
          setError(true);
        });
    } else {
      setSaving(false);
      setSaved(false);
      setError(true);
      setJsonError(true);
    }
  };

  const updateSlideTheme = (themeItem) => {
    if (themeItem.title && themeItem.title !== "") {
      setTheme(themeItem);
      setThemeJson(getThemeJson(themeItem.title));
      setThemeDirectory(
        `${filesystem_root}/${subdomain}/themes/${themeItem.title}`,
      );
    } else {
      setTheme({
        label: props.slide.theme.field_label,
        title: props.slide.theme.label,
        value: props.slide.theme.tid,
      });
      setThemeJson(getThemeJson(props.slide.theme.label));
      setThemeDirectory(
        `${filesystem_root}/${subdomain}/themes/${props.slide?.theme?.label}`,
      );
    }
  };

  const debounceUpdate = debounce(updateSlide, 2000);

  // eslint-disable-next-line
  const debouncedSlideContextUpdate = debounce(() => {
    setContextSlideUpdate(true);
  }, 3000);

  const modalTitle = () => {
    if (props.showLayoutSettings) {
      return "Template Settings";
    }
    if (props.showLogoSettings) {
      return "Logo settings";
    }

    if (props.showBackgroundSettings) {
      return "Background settings";
    }

    if (props.showTransitionSettings) {
      return "Transition settings";
    }

    if (props.showSlideAudioSettings) {
      return "Slide audio";
    }

    if (props.showSlideSettings) {
      return "Slide settings";
    }
    if (props.showHelpDialog) {
      return helpText.header;
    }
    if (props.showSlideNotes) {
      return "Slide notes";
    }
  };

  const slideBackground = () => {
    return (
      <>
        <div
          className={`settings-wrap-bg-main ${
            slideInnerBackgroundImage === "" ? "none" : ""
          }`}
        >
          <BackgroundImagePicker
            user={user}
            presentation={props.presentation}
            slide={props.slide}
            slideLayout={slideLayout}
            title={"Slide background"}
            slideBackgroundImage={slideInnerBackgroundImage}
            background_images={theme_json?.background_images}
            themeDirectory={themeDirectory}
            showTooltip={true}
            onImageSelected={(background_image) => {
              setSlideInnerBackgroundImage(background_image);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            imagePicked={slideInnerBackgroundImage !== ""}
            // opacity settings
            opacityTitle={"Image opacity"}
            slideBackgroundOpacity={slideInnerBackgroundOpacity}
            onBackgroundImageOpacity={(opacity) => {
              setSlideInnerBackgroundOpacity(opacity);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            // size settings
            sizeTitle={"Size"}
            slideBackgroundSize={slideInnerBackgroundSize}
            onBackgroundImageSize={(size) => {
              setSlideInnerBackgroundSize(size);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            // repeat settings
            repeatTitle={"Repeat"}
            slideBackgroundRepeat={slideInnerBackgroundRepeat}
            onBackgroundImageRepeat={(repeat) => {
              setSlideInnerBackgroundRepeat(repeat);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            // position settings
            positionTitle={"Position"}
            slideBackgroundPosition={slideInnerBackgroundPosition}
            onBackgroundImagePosition={(position) => {
              setSlideInnerBackgroundPosition(position);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            sliderMouseDown={() => setTransparentSettings(true)}
            sliderChangeCommit={() => setTransparentSettings(false)}
            transparentSettings={transparentSettings}
          />
        </div>
        <ColorPickerNew
          title={
            slideLayout.id === "section-title-arch-image"
              ? "Overlay colour"
              : "Background colour"
          }
          slideBackgroundColor={slideInnerBackgroundColor}
          colors={getColors(0)}
          colorsSecondary={getColors(1)}
          onColorSelected={(background_color) => {
            setSlideInnerBackgroundColor(background_color);
            setTimeout(() => {
              props.onRevealUpdate();
            }, 10);
          }}
          overlayOpacity={slideOverlayOpacity}
          changeOverlayOpacity={(value) => {
            setSlideOverlayOpacity(value);
          }}
          sliderMouseDown={() => {
            setTransparentSettings(true);
            setTransparentFocus("bg-overlay-opacity");
          }}
          sliderChangeCommit={() => {
            setTransparentSettings(false);
            setTransparentFocus("");
          }}
          transparentSettings={transparentSettings}
          transparentFocus={transparentFocus}
        />
        {theme_json?.patterns && theme_json?.patterns.length > 0 && (
          <div className="pattern-container mb-4">
            <Row>
              <Col
                xs={"auto"}
                className={`d-flex align-items-end pe-4 ${
                  transparentSettings ? "transparent-opacity" : ""
                }`}
              >
                <Typography
                  variant="h5"
                  owner="admin"
                  color="adminText.dark"
                  className="m-0 pb-1 mb-1"
                >
                  Pattern
                </Typography>
              </Col>

              <Col>
                <Row>
                  {theme_json?.patterns.map((e) => (
                    <Col xs={"auto"}>
                      <Tooltip title={e.label}>
                        <Button
                          className={`pattern-button ${
                            slidePattern === e.value ? "selected" : ""
                          }`}
                          onClick={() => setSlidePattern(e.value)}
                        >
                          <img
                            className="pattern-img"
                            src={`${themeDirectory}/dist/images/${e.src}`}
                            alt=""
                          />
                        </Button>
                      </Tooltip>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </div>
        )}

        {permissions?.access_advanced_background_options === true && (
          <Accordion
            elevation={0}
            sx={{
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<KeyboardArrowRight />}
              className={`${transparentSettings ? "transparent-opacity" : ""}`}
              sx={{
                padding: 0,
                flexDirection: "row-reverse",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(90deg)",
                },
                "& .MuiAccordionSummary-content": {
                  marginLeft: 1,
                },
              }}
            >
              <Row className="w-100">
                <Col xs={"auto"}>Advanced</Col>
                <Col
                  xs={"auto"}
                  className="d-flex align-items-center flex-grow-1 p-0"
                >
                  <Divider sx={{ width: 1 }} />
                </Col>
              </Row>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {windowBackground()}
            </AccordionDetails>
          </Accordion>
        )}
      </>
    );
  };

  const windowBackground = () => {
    return (
      <>
        <div
          className={`settings-wrap-bg-main mt-3 ${
            slideBackgroundImage === "" ? "none" : ""
          }`}
        >
          <BackgroundImagePicker
            user={user}
            presentation={props.presentation}
            slide={props.slide}
            title={"Window background"}
            slideBackgroundImage={slideBackgroundImage}
            background_images={theme_json?.background_images}
            themeDirectory={themeDirectory}
            showTooltip={true}
            onImageSelected={(background_image) => {
              setSlideBackgroundImage(background_image);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            imagePicked={slideBackgroundImage !== ""}
            // opacity settings
            opacityTitle={"Image opacity"}
            slideBackgroundOpacity={slideBackgroundOpacity}
            onBackgroundImageOpacity={(opacity) => {
              setSlideBackgroundOpacity(opacity);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            // size settings
            sizeTitle={"Size"}
            slideBackgroundSize={slideBackgroundSize}
            onBackgroundImageSize={(size) => {
              setSlideBackgroundSize(size);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            // repeat settings
            repeatTitle={"Repeat"}
            slideBackgroundRepeat={slideBackgroundRepeat}
            onBackgroundImageRepeat={(repeat) => {
              setSlideBackgroundRepeat(repeat);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            // position settings
            positionTitle={"Position"}
            slideBackgroundPosition={slideBackgroundPosition}
            onBackgroundImagePosition={(position) => {
              setSlideBackgroundPosition(position);
              setTimeout(() => {
                props.onRevealUpdate();
              }, 10);
            }}
            sliderMouseDown={() => setTransparentSettings(true)}
            sliderChangeCommit={() => setTransparentSettings(false)}
            transparentSettings={transparentSettings}
          />
        </div>
        {/* <ColorPicker */}
        <ColorPickerNew
          title={"Background colour"}
          slideBackgroundColor={slideBackgroundColor}
          colors={getColors(0)}
          colorsSecondary={getColors(1)}
          onColorSelected={(background_color) => {
            setSlideBackgroundColor(background_color);
            setTimeout(() => {
              props.onRevealUpdate();
            }, 10);
          }}
          transparentSettings={transparentSettings}
          transparentFocus={transparentFocus}
        />
      </>
    );
  };

  const backgroundSettings = () => {
    if (props.showBackgroundSettings) {
      return <>{slideBackground()}</>;
    }
  };

  const getFormattedDate = (value) => {
    let date = new Date(value * 1000);
    let day = date.getDate().toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    let month = date.getMonth() + 1;
    month = month.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    let year = date.getFullYear();
    let hours = date.getHours().toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    let minutes = date.getMinutes().toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    let formattedDate = `${day}.${month}.${year} (${hours}:${minutes})`;
    return formattedDate;
  };

  const filterAllowedThemes = (slide_layout) => {
    let presentationThemes = props.themes;
    let allowedThemes = getThemeIds(slide_layout);
    let themesFiltered = presentationThemes.filter((obj) =>
      allowedThemes.includes(obj.title),
    );
    return themesFiltered;
  };

  const slideSettingsBase = () => {
    return (
      <>
        <Container className="mt-3 slide-settings-base px-0">
          <Row
            className={`${transparentSettings ? "transparent-opacity" : ""}`}
          >
            <Col className="pe-5">
              <Box className="form-group d-flex flex-column">
                <Typography
                  className="vertical"
                  variant="form-group-label"
                  owner="admin"
                >
                  Slide title
                </Typography>
                <TextField
                  id="slide-title-tf"
                  variant="outlined"
                  fullWidth
                  sx={{ p: 0 }}
                  size="small"
                  value={slideTitle}
                  onChange={(event) => {
                    setSlideTitle(event.target.value);
                    setActiveSlideTitle(event.target.value);
                  }}
                />
              </Box>
              <Box className="form-group d-flex flex-column">
                <Typography
                  className="vertical"
                  variant="form-group-label"
                  owner="admin"
                >
                  Chapter marker
                </Typography>
                <TextField
                  id="chapter-marker-tf"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={slideChapterMarker}
                  onChange={(event) => {
                    setSlideChapterMarker(event.target.value);
                  }}
                />
              </Box>
              <Box className="form-group d-flex flex-column">
                <Box className="d-flex flex-row align-items-center">
                  <Typography
                    className="vertical"
                    variant="form-group-label"
                    owner="admin"
                  >
                    Slide theme
                  </Typography>
                  <Tooltip
                    title="Please note: to comply with brand guidelines, this template may not be available in all themes. Additionally, certain logo options may not be available."
                    placement="right"
                  >
                    <Button className="label-tooltip">
                      <QuestionMarkIcon className="label-tooltip-icon" />
                    </Button>
                  </Tooltip>
                </Box>
                <CategorySelector
                  name="slideThemes"
                  label="Slide theme"
                  categories={filterAllowedThemes(slideLayout.id)}
                  selectedCategory={theme}
                  outlined={true}
                  variant="outlined"
                  size="small"
                  handleCategorySelection={(tid) => {
                    let themeItem = props.themes.find(
                      (item) => item.tid === tid,
                    );
                    updateSlideTheme({
                      label: themeItem.label,
                      title: themeItem.title,
                      value: themeItem.tid,
                    });
                  }}
                />
              </Box>
              <Container className="px-0 slide-settings-checkbox-container">
                <Row className="gx-0">
                  <Col xs={6}>
                    <FormGroup className="form-group mb-0">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={slideAutoAnimate}
                            onChange={(e) => {
                              setSlideAutoAnimate(e.target.checked);
                            }}
                          />
                        }
                        label={
                          <Typography variant="form-group-label" owner="admin">
                            Auto animate slide
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Col>
                  {slideLayout.id === "custom" ? (
                    <>
                      {permissions?.access_disable_duplicate_on_custom ===
                        true && (
                        <Col xs={6}>
                          <FormGroup className="form-group mb-0">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    preventDuplicate === 1 ? true : false
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setPreventDuplicate(1);
                                    } else {
                                      setPreventDuplicate(0);
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="form-group-label"
                                  owner="admin"
                                >
                                  Disable duplicate
                                </Typography>
                              }
                            />
                          </FormGroup>
                        </Col>
                      )}
                    </>
                  ) : (
                    <Col xs={6}>
                      <FormGroup className="form-group mb-0">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={preventDuplicate === 1 ? true : false}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPreventDuplicate(1);
                                } else {
                                  setPreventDuplicate(0);
                                }
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="form-group-label"
                              owner="admin"
                            >
                              Disable duplicate
                            </Typography>
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}
                </Row>
              </Container>
            </Col>
            <Col className="ps-5">
              <Row className="mb-3">
                <Col className="label-container">
                  <Typography variant="form-group-label" owner="admin">
                    Author
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    weight="regular"
                    color="adminText.main"
                  >
                    {props.slide.author}
                  </Typography>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col className="label-container">
                  <Typography variant="form-group-label" owner="admin">
                    Date created
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    weight="regular"
                    color="adminText.main"
                  >
                    {getFormattedDate(props.slide.created)}
                  </Typography>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col className="label-container">
                  <Typography variant="form-group-label" owner="admin">
                    Last modified
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    weight="regular"
                    color="adminText.main"
                  >
                    {getFormattedDate(props.slide.changed)}
                  </Typography>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col className="label-container">
                  <Typography variant="form-group-label" owner="admin">
                    Template
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    weight="regular"
                    color="adminText.main"
                  >
                    {slideLayout?.name}
                  </Typography>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col className="label-container">
                  <Typography variant="form-group-label" owner="admin">
                    Slide ID
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    weight="regular"
                    color="adminText.main"
                  >
                    {props.slide.id}
                  </Typography>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="slide-options-container pt-2">
            {(getContentPositions()?.length > 0 ||
              getOverlayColor()?.length > 0 ||
              slideLayout.id === "image-with-vertical-overlay" ||
              slideLayout.id === "image-with-horizontal-overlay" ||
              slideLayout.id === "one-column-half-circle-image" ||
              slideLayout.id === "campaign-info" ||
              slideLayout.id === "one-column-arch-image" ||
              slideLayout.id === "data-table" ||
              (slideLayout.id === "advanced" &&
                permissions?.access_advanced_slide_settings === true)) && (
              <Col
                xs={12}
                className={`mt-2 mb-3 ${
                  transparentSettings ? "transparent-opacity" : ""
                }`}
              >
                <Typography
                  owner="admin"
                  variant="h6"
                  color="adminText.dark"
                  className="pb-1 slide-options-label"
                >
                  Slide options
                </Typography>
              </Col>
            )}
            <Col xs={12}>
              <Row>
                {getContentPositions()?.length > 0 ? (
                  <Col
                    xs={4}
                    className={`${
                      transparentSettings ? "transparent-opacity" : ""
                    } slide-option`}
                  >
                    <Row className="mb-3 py-1">
                      <Col className="label-container d-flex align-items-center">
                        <Typography variant="form-group-label" owner="admin">
                          Content position
                        </Typography>
                      </Col>
                      <Col>
                        <Select
                          fullWidth
                          className="slide-settings-select"
                          variant="standard"
                          size="small"
                          sx={{
                            "& .MuiMenuItem-root": {
                              fontSize: 14,
                              color: "#415262",
                              fontWeight: "300",
                            },
                          }}
                          value={slideContentPosition}
                          onChange={(e) => {
                            setSlideContentPosition(e.target.value);
                          }}
                        >
                          {getContentPositions().map((i) => {
                            let label = i;

                            if (slideLayout.id === "one-row-image") {
                              if (i === "left") {
                                label = "Text Top / Image Bottom";
                              }

                              if (i === "right") {
                                label = "Image Top / Text Bottom";
                              }
                            } else if (
                              slideLayout.id === "image-with-vertical-overlay"
                            ) {
                              if (i === "left") {
                                label = "Left overlay";
                              }

                              if (i === "right") {
                                label = "Right overlay";
                              }
                            } else if (
                              slideLayout.id === "image-with-horizontal-overlay"
                            ) {
                              if (i === "left") {
                                label = "Top";
                              }

                              if (i === "right") {
                                label = "Bottom";
                              }
                            } else if (
                              slideLayout.id ===
                              "image-with-vertical-gradient-overlay"
                            ) {
                              if (i === "left") {
                                label = "Text left";
                              }

                              if (i === "right") {
                                label = "Text right";
                              }
                            } else if (
                              slideLayout.id === "at-a-glance" ||
                              slideLayout.id === "dynamic-at-a-glance"
                            ) {
                              if (i === "left") {
                                label = "Top";
                              }

                              if (i === "right") {
                                label = "Bottom";
                              }
                            } else {
                              if (i === "left") {
                                label = "Text left / Image right";
                              }

                              if (i === "right") {
                                label = "Image left / Text right";
                              }
                            }

                            return (
                              <MenuItem key={`pos-${i}`} value={i}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </Col>
                    </Row>
                  </Col>
                ) : (
                  ""
                )}
                {slideLayout.id === "image-with-vertical-gradient-overlay" && (
                  <Col className="slide-option">
                    <Row
                      className={`mb-3 py-1 ${
                        transparentFocus === "content-width"
                          ? "transparent-focus"
                          : transparentSettings
                            ? "transparent-opacity"
                            : ""
                      }`}
                    >
                      <Col className="label-container d-flex align-items-center">
                        <Typography variant="form-group-label" owner="admin">
                          Content width
                        </Typography>
                      </Col>
                      <Col>
                        <Slider
                          value={slideContentWidth}
                          marks
                          step={5}
                          min={30}
                          max={70}
                          valueLabelDisplay="auto"
                          onChange={(e) => {
                            setSlideContentWidth(e.target.value);
                          }}
                          onMouseDown={() => {
                            setTransparentSettings(true);
                            setTransparentFocus("content-width");
                          }}
                          onChangeCommitted={() => {
                            setTransparentSettings(false);
                            setTransparentFocus("");
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
                {slideLayout.id === "at-a-glance" && (
                  <HotelCapacityOptions
                    transparentFocus={transparentFocus}
                    transparentSettings={transparentSettings}
                    capacityTags={capacityTags}
                    setCapacityTags={(stateKey) => {
                      setCapacityTags((prevState) => ({
                        ...prevState,
                        [stateKey]: !prevState[stateKey],
                      }));
                    }}
                  />
                )}
              </Row>
              <Row>
                {getOverlayColor()?.length > 0 ? (
                  <Col
                    xs={4}
                    className={`${
                      transparentSettings ? "transparent-opacity" : ""
                    } slide-option`}
                  >
                    <Row className="mb-3">
                      <Col className="label-container d-flex align-items-center">
                        <Typography variant="form-group-label" owner="admin">
                          Overlay colour
                        </Typography>
                      </Col>
                      <Col>
                        <Select
                          fullWidth
                          variant="standard"
                          className="slide-settings-select"
                          sx={{
                            "& .MuiSelect-select": {
                              textTransform: "capitalize",
                            },
                          }}
                          size="small"
                          value={slideOverlayColor}
                          onChange={(e) => {
                            setSlideOverlayColor(e.target.value);
                          }}
                        >
                          {getOverlayColor().map((i) => {
                            return (
                              <MenuItem
                                key={`pos-${i}`}
                                value={i}
                                sx={{ textTransform: "capitalize" }}
                              >
                                {i}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </Col>
                    </Row>
                  </Col>
                ) : (
                  ""
                )}
                {(slideLayout.id === "image-with-vertical-overlay" ||
                  slideLayout.id ===
                    "image-with-vertical-gradient-overlay") && (
                  <OverlayDimensions
                    overlayDimensionLabel={"Overlay width"}
                    transparentFocus={transparentFocus}
                    transparentFocusClass={"overlay-width"}
                    transparentSettings={transparentSettings}
                    slideLayout={slideLayout}
                    overlayDimensionValue={slideOverlayWidth}
                    setTransparentSettings={setTransparentSettings}
                    setTransparentFocus={setTransparentFocus}
                    setOverlayDimensionValue={setSlideOverlayWidth}
                    steps={5}
                    min={30}
                    max={
                      slideLayout.id === "image-with-vertical-gradient-overlay"
                        ? 100
                        : 70
                    }
                  />
                )}
                {slideLayout.id === "image-with-horizontal-overlay" && (
                  <OverlayDimensions
                    overlayDimensionLabel={"Overlay height"}
                    transparentFocus={transparentFocus}
                    transparentFocusClass={"overlay-height"}
                    transparentSettings={transparentSettings}
                    slideLayout={slideLayout}
                    overlayDimensionValue={slideOverlayHeight}
                    setTransparentSettings={setTransparentSettings}
                    setTransparentFocus={setTransparentFocus}
                    setOverlayDimensionValue={setSlideOverlayHeight}
                  />
                )}
                {(slideLayout.id === "image-with-vertical-overlay" ||
                  slideLayout.id === "image-with-horizontal-overlay" ||
                  slideLayout.id ===
                    "image-with-vertical-gradient-overlay") && (
                  <OverlayOpacity
                    transparentFocus={transparentFocus}
                    transparentSettings={transparentSettings}
                    setTransparentSettings={setTransparentSettings}
                    setTransparentFocus={setTransparentFocus}
                    opacityValue={slideOverlayOpacity}
                    setOverlayOpacity={setSlideOverlayOpacity}
                    disableOverlaySteps={getDisableOverlaySteps()}
                  />
                )}
              </Row>
            </Col>

            {(slideLayout.id === "one-column-half-circle-image" ||
              slideLayout.id === "one-column-arch-image") && (
              <Col className="slide-option">
                <Row className="mb-3">
                  <Col xs={"auto"}>
                    {/* <Col className="label-container"> */}
                    <Typography variant="form-group-label" owner="admin">
                      Align content top
                    </Typography>
                  </Col>
                  <Col xs={"auto"}>
                    <Checkbox
                      checked={archContentAlignment}
                      size="small"
                      onChange={(e) => {
                        setArchContentAlignment(e.target.checked);
                      }}
                      sx={{
                        "& .MuiSvgIcon-root": { fontSize: 22 },
                        "&": { padding: "0" },
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            )}
            {slideLayout.id === "data-table" && (
              <Col className="slide-option">
                <Row
                  className={`mb-3 ${
                    transparentFocus === "column-width"
                      ? "transparent-focus"
                      : transparentSettings
                        ? "transparent-opacity"
                        : ""
                  }`}
                >
                  <Col className="label-container d-flex align-items-center">
                    <Typography variant="form-group-label" owner="admin">
                      First column width
                    </Typography>
                  </Col>
                  <Col>
                    <Slider
                      value={slideDataTableColumn}
                      marks
                      step={5}
                      min={0}
                      max={30}
                      valueLabelDisplay="auto"
                      onChange={(e) => {
                        setSlideDataTableColumn(e.target.value);
                      }}
                      onMouseDown={() => {
                        setTransparentSettings(true);
                        setTransparentFocus("column-width");
                      }}
                      onChangeCommitted={() => {
                        setTransparentSettings(false);
                        setTransparentFocus("");
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            )}
            {slideLayout.id === "advanced" &&
              permissions?.access_advanced_slide_settings === true && (
                <FragmentOption
                  fragmentCount={fragmentCount}
                  setFragmentCount={(value) => setFragmentCount(value)}
                />
              )}
          </Row>
          {permissions?.access_advanced_slide_settings === true && (
            <>
              <Accordion
                className={`${
                  transparentSettings ? "transparent-opacity" : ""
                }`}
                elevation={0}
                sx={{
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardArrowRight />}
                  sx={{
                    padding: 0,
                    flexDirection: "row-reverse",
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(90deg)",
                    },
                    "& .MuiAccordionSummary-content": {
                      marginLeft: 1,
                    },
                  }}
                >
                  <Row className="w-100">
                    <Col xs={"auto"}>Advanced</Col>
                    <Col
                      xs={"auto"}
                      className="d-flex align-items-center flex-grow-1 p-0"
                    >
                      <Divider sx={{ width: 1 }} />
                    </Col>
                  </Row>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0,
                  }}
                >
                  {slideSettingsAdvanced()}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Container>
      </>
    );
  };

  const slideSettingsAdvanced = () => {
    return (
      <Row className="slide-settings-adv">
        <Col xs={12}>{slideLayoutSettings()}</Col>
        <Col xs={12} sm={6}>
          <BespokeMedia
            user={user}
            files={{
              minWidth: 400,
              minHeight: 225,
              maxWidth: 400,
              maxHeight: 225,
              aspectRatio: 1.777777777777778,
            }}
            media={slideThumbnail}
            onMediaInsert={(media) => {
              setSlideThumbnail(media);
            }}
            title={`Add slide thumbnail`}
            hint={`To attach a bespoke thumbnail to the slide, please select or upload an image file via the media library`}
          />
        </Col>
      </Row>
    );
  };

  const slideLayoutSettings = () => {
    let layouts_sorted = theme_json?.layouts.sort((a, b) => {
      let na = a.name.toLowerCase(),
        nb = b.name.toLowerCase();

      if (na < nb) {
        return -1;
      }
      if (na > nb) {
        return 1;
      }
      return 0;
    });
    if (permissions?.access_advanced_layout) {
      return (
        <Row className="mt-2 mb-4">
          <Col>
            <Row className="mb-2">
              <Typography variant="h6" owner="admin" color="adminText.dark">
                Slide Layout
              </Typography>
            </Row>
            <FormControl fullWidth size="small">
              <InputLabel
                sx={{ fontSize: 14 }}
                size="small"
                id="layout-select-label"
              >
                Layout
              </InputLabel>
              <Select
                labelId="layout-select-label"
                id="layout-select"
                value={slideLayout.id}
                label="Layout"
                variant={"outlined"}
                onChange={(event) => {
                  theme_json?.layouts?.forEach((layout) => {
                    if (layout.id === event.target.value) {
                      setSlideLayout(layout);
                    }
                  });
                }}
                input={<OutlinedInput sx={{ fontSize: 14 }} label="Language" />}
              >
                {layouts_sorted?.map((layout, index) => (
                  <MenuItem
                    key={`layout-${index}`}
                    value={layout.id}
                    name={layout.name}
                    sx={{ fontSize: 14 }}
                  >
                    {layout.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Col>
        </Row>
      );
    }
  };

  const slideSettings = () => {
    if (props.showSlideSettings) {
      return <>{slideSettingsBase()}</>;
    }
  };

  const slideNotesInput = () => {
    return (
      <div className="slide-notes-modal-content">
        <TextField
          multiline
          defaultValue={slideNotes}
          variant="standard"
          autoFocus={true}
          value={slideNotes ? slideNotes : ""}
          className="slide-notes-input"
          onChange={(event) => {
            setSlideNotes(event.target.value);
          }}
          placeholder='You can leave notes for the slide here. This will show up on "Speaker View". In the viewer, press "S" on your keyboard to activate.'
        />
      </div>
    );
  };

  const MemoImg = React.memo(function Image({ src, className, alt, overlay }) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={{
          filter: `saturate(${overlay === 1 ? 1 : 0}) opacity(${
            overlay ? overlay : 1
          })`,
        }}
      />
    );
  });

  const MemoImage = React.memo(function Image({ href, width, height }) {
    return (
      <image
        href={href}
        width={width}
        height={height}
        mask={`${slideInnerBackgroundColor ? "url(#mask)" : ""}`}
      ></image>
    );
  });

  const SectionTitleArchImage = () => {
    return (
      <div className="image-container">
        <MemoImg
          src={backgroundImageURIGenerator(
            slideInnerBackgroundImage,
            false,
            props.slide,
            props.presentation.id,
          )}
          className={"image"}
          alt=""
          overlay={slideOverlayOpacity}
        />

        <div
          className="colour-filter"
          style={{ backgroundColor: slideInnerBackgroundColor }}
        ></div>

        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1366 768"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <defs>
            <mask id="mask">
              <path
                fill="white"
                d="M1278.91,768H786c.29-.94,61.53-255.72,18.09-460.58C766.76,131.56,622.58.55,622,0h492c2.19,3.75,141.44,131.92,176.94,298.05,45,210.42-12.94,468.85-12.94,468.85C1278.05,767.23,1279,767.67,1278.91,768Z"
              />
            </mask>
          </defs>

          <MemoImage
            href={backgroundImageURIGenerator(
              slideInnerBackgroundImage,
              false,
              props.slide,
              props.presentation.id,
            )}
            width="1366"
            height="768"
          />
        </svg>
      </div>
    );
  };

  const OneColumArchImage = () => {
    return (
      <div className="overlay-container">
        <svg
          id="Layer_1"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 1080"
        >
          <path
            fill="#fff"
            d="M1104.72,1079.73c-365.62,0-732.66-.09-1098.29,0-5.3,0-6.47-1.16-6.47-6.46Q.2,540,0,6.73C0,1.44,1.13.27,6.43.27c249,.15,618.79,0,867.78,0,.85.78,203.5,184.91,255.93,432.1C1191.2,720.3,1105.12,1078.41,1104.72,1079.73Z"
            transform="translate(0.04 -0.27)"
          />
          <path
            fill="#fff"
            d="M1565.74.27q174.3,0,348.51,0c4.81,0,5.76,1,5.75,5.76q-.21,534,0,1067.94c0,4.82-.93,5.8-5.75,5.76-38.88-.25-77.75,0-116.66,0,.11-.46-1.21-1.08-1.33-1.54,0,0,81.38-363.24,18.18-659C1764.55,185.69,1568.82,5.54,1565.74.27Z"
            transform="translate(0.04 -0.27)"
          />
        </svg>
      </div>
    );
  };

  const OneColumnHalfCircleImage = () => {
    switch (theme.title) {
      case "rhg":
        return (
          <div className="overlay-container">
            <svg
              id="Layer_1"
              data-name="Layer 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1366 768"
            >
              <rect fill="transparent" width="1366" height="768" />
              <path
                fill="#fff"
                d="M773.09,767.91q-384.24,0-768.5.09C.84,768,0,767.16,0,763.41Q.15,384,0,4.59C0,.84.84,0,4.59,0q384.26.15,768.5.09c-10,22.58-20.43,45-29.8,67.79-6.46,15.74-11.44,32.1-17.08,48.18l-5.67,17.84q-5.85,21.87-11.69,43.75-4.7,21.28-9.38,42.56L697.19,233l-.93,5-6.45,45.87-1.88,18.66c-.07.44-.14.88-.22,1.32l-.45,4.08q-.48,6-1,12.05-1,26-2.11,51.95c.57,23.63.61,47.28,1.83,70.88,2.15,41.78,8.23,83.09,17.16,124,3.69,16.85,8.34,33.48,12.55,50.21l5.49,18.85,1,4c.54,1.32,1.09,2.64,1.63,4l.12.06q1.51,5.08,3,10.17l1.23,5,1,2.87.91,2.88q1.08,2.12,2.17,4.23,1.15,4,2.32,8c.17.65.35,1.3.53,2,.53,1.37,1.05,2.75,1.58,4.12l.42,1.75,2.72,7.23,5.28,12.77c2.51,6.33,4.94,12.68,7.61,18.93.31.71,2,.85,3,1.26,0,1.3.07,2.6.11,3.91-.12.1-.35.25-.33.29a6.1,6.1,0,0,0,.41.7l3.17,7.06,2.1,5,1.87,4,2.06,4.89c1,2,2,4,3,6Z"
                transform="translate(0)"
              />
            </svg>
          </div>
        );
      case "companyapp":
      case "companyapp_2025":
      default:
        return (
          <div className="overlay-container">
            <svg
              id="Layer_1"
              data-name="Layer 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1920 1080"
            >
              <title>full-circle</title>
              <path
                fill="#fff"
                stroke="none"
                d="M0,0V1080H1920V0ZM1440,946c-224,0-405.5-181.55-405.5-405.5S1216.05,135,1440,135s405.5,181.55,405.5,405.5S1664,946,1440,946Z"
              />
            </svg>
          </div>
        );
    }
  };

  const ImageWithVerticalGradientOverlay = () => {
    return (
      <div
        className={`overlay-container alpha-${Math.round(
          slideOverlayOpacity * 10,
        )} ${slideOverlayColor} overlay-${slideOverlayWidth} ${slideContentPosition}`}
      />
    );
  };

  const customLayoutPermission = () => {
    const customLayout = theme_json?.layouts.find((o) => {
      return o.id === "custom";
    });
    const whitelistArr = customLayout?.whitelist ? customLayout?.whitelist : [];
    if (
      userAuth &&
      userAuth?.roles_target_id !== "" &&
      userAuth?.roles_target_id !== "undefined"
    ) {
      if (whitelistArr.length > 0) {
        if (whitelistArr.some((r) => userAuth?.roles_target_id.includes(r))) {
          return true;
        }
      } else if (userAuth?.roles_target_id.includes("administrator")) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  };

  const getColors = (variant, icons) => {
    if (theme_json && theme_json?.layouts) {
      let slideLayoutObj = theme_json?.layouts.find((o) => {
        return o.id === slideLayout.id;
      });
      let colorsPrimary = slideLayoutObj?.colors;
      let colorsSecondary = slideLayoutObj?.colorsSecondary;
      if (icons) {
        if (variant === 0 && slideLayoutObj?.iconColors) {
          colorsPrimary = slideLayoutObj?.iconColors;
        }
        if (variant === 1 && slideLayoutObj?.iconColorsSecondary) {
          colorsSecondary = slideLayoutObj?.iconColorsSecondary;
        }
      }
      return variant ? colorsSecondary : colorsPrimary;
    } else {
      return variant ? slideLayout.colorsSecondary : slideLayout.colorsPrimary;
    }
  };

  const getDisableOverlaySteps = () => {
    if (theme_json && theme_json?.layouts && theme_json?.layouts?.length > 0) {
      const foundLayout = theme_json.layouts.find(
        (e) => e.id === slideLayout.id,
      );
      if (foundLayout) {
        return foundLayout.disableOverlaySteps;
      }
    }
    return null;
  };

  const getContentPositions = () => {
    if (theme_json && theme_json?.layouts) {
      let slideLayoutObj = theme_json?.layouts.find((o) => {
        return o.id === slideLayout.id;
      });
      return slideLayoutObj?.contentPosition;
    } else {
      return slideLayout?.contentPosition ? slideLayout?.contentPosition : [];
    }
  };

  const getOverlayColor = () => {
    if (theme_json && theme_json?.layouts) {
      let slideLayoutObj = theme_json?.layouts.find((o) => {
        return o.id === slideLayout.id;
      });
      return slideLayoutObj?.overlayColor;
    } else {
      return slideLayout?.overlayColor ? slideLayout?.overlayColor : [];
    }
  };

  const testIframe = (src) => {
    fetch(src).then((res) => {
      if (res.status === 404 || res.status === 403 || res.status === 500) {
        setIframeError(true);
      }
    });
  };

  let bgStyling = {
    backgroundSize: slideInnerBackgroundSize,
    backgroundRepeat: slideInnerBackgroundRepeat,
    backgroundPosition: slideInnerBackgroundPosition,
    opacity:
      slideLayout.id !== "one-column-arch-image" &&
      slideLayout.id !== "one-column-half-circle-image" &&
      slideLayout.id !== "campaign-info" &&
      slideInnerBackgroundOpacity,
    backgroundImage:
      slideLayout.id === "one-column-arch-image" ||
      slideLayout.id === "one-column-half-circle-image" ||
      slideLayout.id === "campaign-info"
        ? ""
        : slideInnerBackgroundImage !== ""
          ? `url(${backgroundImageURIGenerator(
              slideInnerBackgroundImage,
              false,
              props.slide,
              props.presentation.id,
            )})`
          : "",
  };

  // let dataAutoAnimate = slideAutoAnimate ? {
  //   'data-auto-animate': ''
  // } : {}

  const helpTextParser = (string) => {
    if (string) {
      const parsed = string.replace(
        new RegExp("ASSET_PREFIX", "g"),
        HELP_TEXT_IMAGE_ROOT,
      );
      return parsed;
    } else {
      return string;
    }
  };

  const lockupEnabled = () => {
    if (client === "revo-h") {
      return true;
    }
    return (
      props.slide?.theme?.label === "rhg" && slideLayout.id === "thank-you"
    );
  };

  const isImgOverlay = () => {
    return (
      slideLayout.id === "image-with-horizontal-overlay" ||
      slideLayout.id === "image-with-vertical-overlay"
    );
  };

  const capacityString = () => {
    let classString = "";
    Object.keys(capacityTags).forEach((key) => {
      if (capacityTags[key]) {
        classString = `${classString} capacity-${key}`;
      }
    });
    return classString;
  };

  const innerContainerClasses = () => {
    let classString = `inner-container editor-container content-${contentCount} ${
      hasImage ? "has-image" : ""
    }`;
    if (popupCount > 0) {
      classString += ` popup-${popupCount}`;
    }
    if (theme.title === "artotel") {
      classString += ` ${slidePattern}`;
    }
    if (isImgOverlay()) {
      classString += ` ${slideOverlayColor}`;
      classString += ` alpha-${Math.round(slideOverlayOpacity * 10)}`;
    }
    if (slideContentPosition !== "") {
      classString += ` ${slideContentPosition}`;
    }
    if (slideLayout.id === "data-table") {
      classString += ` stub-column-${
        slideDataTableColumn ? slideDataTableColumn : 0
      }`;
    }
    if (
      slideLayout.id === "one-column-half-circle-image" ||
      slideLayout.id === "campaign-info" ||
      slideLayout.id === "one-column-arch-image"
    ) {
      classString += ` ${archContentAlignment ? "top" : ""}`;
    }
    if (slideLayout.id === "image-with-vertical-gradient-overlay") {
      classString += ` content-${slideContentWidth}`;
    }
    if (slideLayout.id === "at-a-glance") {
      classString += ` ${capacityString()}`;
    }
    if (jsonError || remoteJsonError) {
      classString += ` d-none`;
    }
    if (slideGrid) {
      classString += ` slide-setting-grid`;
    }
    if (showBlockOutlines) {
      classString += ` show-outlines`;
    }
    if (slideLayout.id === "custom" && !customLayoutPermission()) {
      classString += ` disable-edit`;
    }
    return classString;
  };

  const dispatchBlockChange = (_block, _index) => {
    const blockId = _block.getAttribute("data-id");
    const blocksApi = editorInstance.current.blocks;

    if (!blockId) return;

    const count = blocksApi.getBlocksCount();
    let currentIndex = -1;

    for (let i = 0; i < count; i++) {
      const block = blocksApi.getBlockByIndex(i);
      if (block && block.id === blockId) {
        currentIndex = i;
        break;
      }
    }

    if (currentIndex === -1) return;

    // Move block if target index is provided
    if (typeof _index === "number" && currentIndex !== _index) {
      const targetIndex = Math.max(0, Math.min(_index, count - 1));
      blocksApi.move(targetIndex, currentIndex);
    }

    // Notify Editor.js that something changed
    const blockApi = blocksApi.getById(blockId);
    blockApi?.dispatchChange();
  };

  const groupSelectedBlocks = () => {
    if (selectedBlocks.size > 0) {
      const groupId = (
        Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
      ).slice(-8);

      let _index = editorInstance.current.blocks.getBlocksCount() - 1;

      const sortIdsByIndex = () => {
        const blocksArray = Array.from(selectedBlocks);
        blocksArray.sort((a, b) => {
          const indexA = editorInstance.current.blocks.getBlockIndex(a);
          const indexB = editorInstance.current.blocks.getBlockIndex(b);
          return indexB - indexA;
        });
        return blocksArray;
      };

      const sortedBlockIds = sortIdsByIndex();

      for (const blockId of sortedBlockIds) {
        const block = slideRef.current.querySelector(
          `.ce-block[data-id="${blockId}"]`,
        );
        block.setAttribute("data-group", `group-${props.slide.id}-${groupId}`);
        dispatchBlockChange(block, _index);
        _index--;
      }

      if (selectedGroupIds.size > 0) {
        for (const _groupId of selectedGroupIds) {
          const ungroupedBlocks = slideRef.current.querySelectorAll(
            `.ce-block[data-group="${_groupId}"]`,
          );
          const ungroupedBlockFrame = slideRef.current.querySelector(
            `.group-frame[data-group-frame-id="frame-${_groupId}"]`,
          );
          ungroupedBlocks.forEach((_block) => {
            _block.removeAttribute("data-group");
          });
          interact(ungroupedBlockFrame).unset();
          ungroupedBlockFrame.remove();
        }
      }
      setSelectedGroupIds(() => {
        const next = new Set();
        next.add(`group-${props.slide.id}-${groupId}`);
        return next;
      });
      slideRef.current
        .querySelectorAll(".show-group:not([data-group])")
        .forEach((block) => {
          block.classList.remove("show-group");
        });
      wrapBlockGroup(`group-${props.slide.id}-${groupId}`);
    }
  };

  const ungroupSelectedBlocks = () => {
    if (selectedGroupIds.size === 0) return;
    const slide = slideRef.current;

    // Remove grouping from all blocks in those groups
    for (const groupId of selectedGroupIds) {
      const groupedBlocks = slide.querySelectorAll(
        `.ce-block[data-group="${groupId}"]`,
      );

      groupedBlocks.forEach((block) => {
        const groupFrame = slideRef.current.querySelector(
          `.group-frame[data-group-frame-id="frame-${groupId}"]`,
        );
        if (groupFrame) {
          groupFrame.remove();
        }
        block.removeAttribute("data-group");
        if (block.classList.contains("show-group")) {
          block.classList.remove("show-group");
        }
        dispatchBlockChange(block);
      });
    }
    setSelectedGroupIds(() => new Set());
    slideRef.current
      .querySelectorAll(".show-group:not([data-group])")
      .forEach((block) => {
        block.classList.remove("show-group");
      });
  };

  const addBlock = (blockType) => {
    if (!editorInstance.current) return;

    const editor = editorInstance.current;
    const blockCount = editor.blocks.getBlocksCount();
    editor.blocks.insert(blockType, {}, {}, blockCount, false);
  };

  const duplicateBlock = async (blockId, newGroupIds) => {
    if (!editorInstance.current) return null;

    const editor = editorInstance.current;

    try {
      const blockIndex = editor.blocks.getBlockIndex(blockId);
      if (blockIndex === -1) return null;

      const block = editor.blocks.getBlockByIndex(blockIndex);
      const blockCount = editor.blocks.getBlocksCount();
      const savedBlock = await block.save();

      await editor.blocks.insert(savedBlock.tool, {}, {}, blockCount, false);

      const newBlock = editor.blocks.getBlockByIndex(blockCount);
      const newBlockId = newBlock.id;

      const savedData = await editor.save();
      const blockTunes = savedData.blocks[blockIndex].tunes;

      const draggableData = blockTunes?.DragResize?.draggable
        ? { ...blockTunes?.DragResize?.draggable }
        : null;

      if (draggableData) {
        const newDraggableData = {
          ...draggableData,
          top: parseInt(draggableData.top || 0) + 50,
          left: parseInt(draggableData.left || 0) + 50,
          group:
            newGroupIds[draggableData.group] ||
            newGroupIds[draggableData.group] === null
              ? newGroupIds[draggableData.group]
              : draggableData.group,
        };

        await editor.blocks.update(newBlockId, savedBlock.data, {
          ...blockTunes,
          DragResize: { draggable: newDraggableData },
        });
      } else {
        await editor.blocks.update(newBlockId, savedBlock.data, blockTunes);
      }

      return newBlockId;
    } catch (error) {
      console.error("Error duplicating block:", error);
      return null;
    }
  };

  const duplicateBlockQueue = async () => {
    if (selectedBlocks.size === 0) return;

    const selectedBlocksArr = Array.from(selectedBlocks);
    const newBlockSelection = new Set();
    const tempGroupIds = Array.from(selectedGroupIds);
    const newGroupIds = {};
    if (tempGroupIds.length > 0) {
      for (const groupId of tempGroupIds) {
        const selectedElements = slideRef.current.querySelectorAll(
          `[data-group="${groupId}"].selected`,
        );
        if (selectedElements.length > 1) {
          const newGroupId = `group-${props.slide.id}-${(
            Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
          ).slice(-8)}`;
          newGroupIds[groupId] = newGroupId;
        } else {
          newGroupIds[groupId] = null;
        }
      }
    }

    for (const blockId of selectedBlocksArr) {
      const newId = await duplicateBlock(blockId, newGroupIds);

      if (newId) {
        newBlockSelection.add(newId);
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    const newGroupIdsSet = new Set(Object.values(newGroupIds).filter(Boolean));
    setSelectedBlocks(newBlockSelection);
    setSelectedGroupIds(newGroupIdsSet);
  };

  const toggleBlock = (blockId) => {
    setSelectedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  };

  const hideBlock = (blockId) => {
    const block = slideRef.current.querySelector(
      `.ce-block[data-id="${blockId}"]`,
    );
    if (block.getAttribute("data-hide") === "true") {
      block.removeAttribute("data-hide");
      dispatchBlockChange(block);
    } else {
      block.setAttribute("data-hide", "true");
      dispatchBlockChange(block);
    }
  };

  const hideGroup = (groupId) => {
    const blocks = slideRef.current.querySelectorAll(
      `.ce-block[data-group="${groupId}"]`,
    );
    if (blocks.length > 0) {
      if (
        [...blocks].filter(
          (block) => block.getAttribute("data-group-hide") === "true",
        ).length > 0
      ) {
        blocks.forEach((block) => {
          block.removeAttribute("data-group-hide");
          dispatchBlockChange(block);
        });
      } else {
        blocks.forEach((block) => {
          block.setAttribute("data-group-hide", "true");
          dispatchBlockChange(block);
        });
      }
    }
  };

  const deleteSelectedBlocks = () => {
    if (selectedBlocks.size === 0) return;
    const blocksToDelete = Array.from(selectedBlocks);
    const blockIndices = blocksToDelete.map((id) =>
      editorInstance.current.blocks.getBlockIndex(id),
    );
    blockIndices.sort((a, b) => b - a);
    for (const blockIndex of blockIndices) {
      editorInstance.current.blocks.delete(blockIndex);
    }
    setSelectedBlocks(new Set());
    setSelectedGroupIds(new Set());
  };

  const customClickHandler = (selectionType, elementId) => {
    if (selectionType === "block" && elementId) {
      toggleBlock(elementId);
    } else if (selectionType === "group-frame" && elementId) {
      const groupId = elementId.replace("frame-", "");
      const groupBlocks = slideRef.current.querySelectorAll(
        `[data-group="${groupId}"]`,
      );
      setSelectedGroupIds((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        return next;
      });
      groupBlocks.forEach((block) => {
        const blockId = block.dataset.id;
        toggleBlock(blockId);
      });
    }
  };

  const customClickListener = (e) => {
    const clickedBlock = e.target.closest(".ce-block");
    const closestToolbar = e.target.closest(".ce-toolbar");
    const groupFrame = e.target.classList.contains("group-frame")
      ? e.target
      : null;

    if (!clickedBlock && !closestToolbar) {
      slideRef.current
        .querySelectorAll(".ce-block--focused")
        .forEach((block) => {
          block.classList.remove("ce-block--focused");
        });
      const editorToolbar = slideRef.current.querySelector(".ce-toolbar");
      if (editorToolbar) {
        editorToolbar.style.top = "unset";
        editorToolbar.classList.remove("ce-toolbar--opened");
      }
    }

    if ((!clickedBlock && !groupFrame) || !selectMode) {
      if (selectedBlocks.size > 0) setSelectedBlocks(new Set());
      if (selectedGroupIds.size > 0) setSelectedGroupIds(new Set());
      return;
    }
    if (clickedBlock) {
      const blockId = clickedBlock.dataset.id;
      customClickHandler("block", blockId);
    }
    if (groupFrame) {
      customClickHandler(
        "group-frame",
        groupFrame.getAttribute("data-group-frame-id"),
      );
    }
  };

  const startSelectMode = () => {
    if (selectMode === false) {
      setSelectMode(true);
      if (!slideRef.current.classList.contains("select-mode")) {
        slideRef.current.classList.add("select-mode");
      }
    }
  };

  const endSelectMode = () => {
    if (selectMode) {
      setSelectMode(false);
      slideRef.current.classList.remove("select-mode");
    }
  };

  const handlePointerDown = (e) => {
    pointerDownRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
    isDraggingRef.current = false;
  };

  const handlePointerMove = (e) => {
    const dx = Math.abs(e.clientX - pointerDownRef.current.x);
    const dy = Math.abs(e.clientY - pointerDownRef.current.y);

    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
      isDraggingRef.current = true;
    }
  };

  const interactStart = (targetArr, slide) => {
    const slideRect = slide.getBoundingClientRect();
    const blocksToMove = Array.from(
      slide.querySelectorAll(".ce-block.selected"),
    );
    const setPosition = (blockArr) => {
      blockArr.forEach((targetBlock) => {
        targetBlock.classList.add("selected");
        const targetRect = targetBlock.getBoundingClientRect();

        targetBlock.dataset.x = targetRect.left - slideRect.left;
        targetBlock.dataset.y = targetRect.top - slideRect.top;
      });
    };
    setPosition([...targetArr, ...blocksToMove]);
  };

  const interactMove = (blocksToMove, e) => {
    const gridSize = 5;
    blocksToMove.forEach((block) => {
      if (!block.snapOffset) block.snapOffset = { x: 0, y: 0 };

      block.snapOffset.x += e.dx;
      block.snapOffset.y += e.dy;

      let x = parseFloat(block.dataset.x) + block.snapOffset.x;
      let y = parseFloat(block.dataset.y) + block.snapOffset.y;

      // Alt-key snapping
      if (e.altKey) {
        const snappedX = Math.round(x / gridSize) * gridSize;
        const snappedY = Math.round(y / gridSize) * gridSize;

        // Only update offset when a grid step has been crossed
        block.snapOffset.x -= snappedX - parseFloat(block.dataset.x);
        block.snapOffset.y -= snappedY - parseFloat(block.dataset.y);

        x = snappedX;
        y = snappedY;
      } else {
        block.snapOffset.x = 0;
        block.snapOffset.y = 0;
      }

      // Apply position
      block.style.left = `${x}px`;
      block.style.top = `${y}px`;

      block.dataset.x = x;
      block.dataset.y = y;
    });
  };

  const interactEnd = (blocksToMove, blocksApi) => {
    blocksToMove.forEach((block) => {
      const blockApi = blocksApi.getById(block.dataset.id);
      blockApi?.dispatchChange();
    });
  };

  const wrapBlockGroup = (groupId) => {
    if (!slideRef.current) return;
    const groupBlocks = slideRef.current.querySelectorAll(
      `[data-group="${groupId}"]`,
    );
    const lastBlockFromGroup = groupBlocks[groupBlocks.length - 1];
    const existingGroupFrame = slideRef.current.querySelector(
      `.group-frame[data-group-frame-id="frame-${groupId}"`,
    );
    let groupFrame;
    if (existingGroupFrame) {
      groupFrame = existingGroupFrame;
      lastBlockFromGroup.after(groupFrame);
    } else {
      groupFrame = document.createElement("div");
      groupFrame.classList.add("group-frame");
      groupFrame.setAttribute("data-group-frame-id", `frame-${groupId}`);
      lastBlockFromGroup.after(groupFrame);
      groupFrame.addEventListener("mouseenter", () => {
        groupBlocks.forEach((b) => {
          b.classList.add("show-group");
        });
      });
      groupFrame.addEventListener("mouseleave", () => {
        groupBlocks.forEach((b) => {
          b.classList.remove("show-group");
        });
      });

      interact(groupFrame)
        .draggable({
          ignoreFrom: ".selected",
          listeners: {
            start(event) {
              interactStart([...groupBlocks, groupFrame], slideRef.current);
            },
            move(event) {
              const blocksToMove = Array.from(
                slideRef.current.querySelectorAll(
                  `.ce-block.selected[data-group="${groupId}"]`,
                ),
              );
              blocksToMove.push(event.target);

              interactMove(blocksToMove, event);
            },
            end(event) {
              const blocksToMove = Array.from(
                slideRef.current.querySelectorAll(".ce-block.selected"),
              );
              const blocksApi = editorInstance.current.blocks;
              interactEnd(blocksToMove, blocksApi);
              slideRef.current
                .querySelectorAll(`.ce-block.selected[data-group="${groupId}"]`)
                .forEach((b) => b.classList.remove("selected"));
              groupFrame.classList.remove("selected");
            },
          },
        })
        .on("tap", (event) => {
          if (!event.shiftKey) {
            setFocusedGroup(groupId);
          } else {
          }
        })
        .on("down", (event) => {
          event.preventDefault();
        });
    }

    modifyGroupFrame(groupFrame, groupId);
  };

  const handleSelector = () => {
    const modifySelector = () => {
      const getValue = (v) => parseFloat(v) || 0;

      let minTop = Infinity;
      let minLeft = Infinity;
      let maxRight = -Infinity;
      let maxBottom = -Infinity;

      selectedBlocks.forEach((block) => {
        const style = slideRef.current.querySelector(
          `.ce-block[data-id="${block}"]`,
        ).style;

        const top = getValue(style.top);
        const left = getValue(style.left);
        const width = getValue(style.width);
        const height = getValue(style.height);

        const right = left + width;
        const bottom = top + height;

        minTop = Math.min(minTop, top);
        minLeft = Math.min(minLeft, left);
        maxRight = Math.max(maxRight, right);
        maxBottom = Math.max(maxBottom, bottom);
      });

      selectorRef.current.style.position = "absolute";
      selectorRef.current.style.top = `${minTop}px`;
      selectorRef.current.style.left = `${minLeft}px`;
      selectorRef.current.style.width = `${maxRight - minLeft}px`;
      selectorRef.current.style.height = `${maxBottom - minTop}px`;
    };
    if (!selectorRef.current) {
      const selectorFrame = document.createElement("div");
      selectorFrame.classList.add("selector-frame");
      selectorRef.current = selectorFrame;
      slideRef.current
        .querySelector(".codex-editor__redactor")
        .appendChild(selectorRef.current);
    }
    modifySelector();
  };

  const processBlockGroups = () => {
    const groups = new Set();
    const groupedBlocks = slideRef.current.querySelectorAll("[data-group");
    if (groupedBlocks.length === 0) return;
    groupedBlocks.forEach((block) => {
      groups.add(block.dataset.group);
    });
    for (const groupId of groups) {
      wrapBlockGroup(groupId);
    }
  };

  const modifyGroupFrame = (groupFrame, groupId) => {
    if (!slideRef.current || !groupFrame) return;

    const groupedBlocks = slideRef.current.querySelectorAll(
      `.ce-block[data-group="${groupId}"]`,
    );

    if (!groupedBlocks.length) return;

    const getValue = (v) => parseFloat(v) || 0;

    let minTop = Infinity;
    let minLeft = Infinity;
    let maxRight = -Infinity;
    let maxBottom = -Infinity;

    groupedBlocks.forEach((block) => {
      const style = block.style;

      const top = getValue(style.top);
      const left = getValue(style.left);
      const width = getValue(style.width);
      const height = getValue(style.height);

      const right = left + width;
      const bottom = top + height;

      minTop = Math.min(minTop, top);
      minLeft = Math.min(minLeft, left);
      maxRight = Math.max(maxRight, right);
      maxBottom = Math.max(maxBottom, bottom);
    });

    groupFrame.style.position = "absolute";
    groupFrame.style.top = `${minTop}px`;
    groupFrame.style.left = `${minLeft}px`;
    groupFrame.style.width = `${maxRight - minLeft}px`;
    groupFrame.style.height = `${maxBottom - minTop}px`;
  };

  const handlePointerUp = (e) => {
    if (!isDraggingRef.current) {
      customClickListener(e);
    }

    // hide all editor block toolbars
    const toolbars = slideRef.current.querySelectorAll(
      ".editor-block-toolbar.show",
    );
    if (e.target.closest(".ce-block")) {
      const closestBlock = e.target.closest(".ce-block");
      toolbars.forEach((toolbar) => {
        if (closestBlock.contains(toolbar)) {
          // do nothing
        } else {
          toolbar.classList.remove("show");
        }
      });
    } else if (toolbars.length > 0) {
      toolbars.forEach((toolbar) => {
        toolbar.classList.remove("show");
      });
    }

    // reset
    isDraggingRef.current = false;
  };

  const handleMouseOver = (e) => {
    if (e.target.closest(".ce-block")) {
      const block = e.target.closest(".ce-block");
      setHoveredBlock({
        id: block.dataset.id,
        groupId: block.dataset.group ?? null,
      });
    } else if (e.target.closest(".group-frame")) {
      const groupFrame = e.target.closest(".group-frame");
      setHoveredBlock({
        id: null,
        groupId:
          groupFrame
            .getAttribute("data-group-frame-id")
            .replace("frame-", "") ?? null,
      });
    } else {
      setHoveredBlock(null);
    }
  };

  return (
    <>
      <section
        ref={slideRef}
        className={`slide-wrap h-100 ${
          permissions.access_blank_layout ? "high-level" : ""
        } ${isSaving ? "" : ""}`}
        data-permissions={permissions?.create_new_block === true ? true : false}
        // reveal params
        data-transition={slideTransition}
        data-transition-speed={slideTransitionSpeed}
        data-background-transition={slideBackgroundTransition}
        data-background-color={slideBackgroundColor}
        data-background-image={backgroundImageURIGenerator(
          slideBackgroundImage,
          false,
          props.slide,
          props.presentation.id,
        )}
        data-background-opacity={slideBackgroundOpacity}
        data-background-repeat={slideBackgroundRepeat}
        data-background-position={slideBackgroundPosition}
        data-background-size={slideBackgroundSize}
        data-theme={theme.title}
        data-slide-layout={slideLayout.id}
        initial-presentation-id={
          isLibrarySlide ? props.slide.initial_presentation : ""
        }
        tabIndex={slideLayout.id === "custom" ? 0 : -1}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onMouseOver={handleMouseOver}
      >
        <div
          id={`slide-${props.slide.id}`}
          className={`slide ${isSafari ? "safari" : ""}`}
        >
          {(jsonError || remoteJsonError) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Box sx={{ maxWidth: "480px" }}>
                <Typography
                  variant="h5"
                  sx={{ color: "#fff", textAlign: "center" }}
                >
                  {jsonError &&
                    `Sorry, it looks like there is a problem with the data for this slide. As a result, we could not save your changes. Please REFRESH the page and try again. If the problem persists, please contact support@companyapp.co.uk quoting "Slide ${props.slide.id}".`}

                  {remoteJsonError &&
                    `Sorry, it looks like there is a problem with the data for this slide. Please contact support@companyapp.co.uk quoting "Slide ${props.slide.id}".`}
                </Typography>
              </Box>
            </Box>
          )}

          {isLibrarySlide && (
            <Tooltip
              id="library-slide-tooltip"
              title={`P=${props.slide.initial_presentation}, S=${props.slide.id}`}
              placement="bottom-start"
            >
              <div className="library-slide-indicator">
                <span className="icon-container">
                  <span className="icon">
                    {/* <Attachment color='solid' /> */}
                    <NounLink
                      style={{
                        transform: "rotate(45deg)",
                        pointerEvents: "none",
                      }}
                    />
                  </span>
                  <span className="tri" />
                </span>
              </div>
            </Tooltip>
          )}

          <div
            className="slide-inner-background"
            style={{ backgroundColor: slideInnerBackgroundColor }}
          >
            <div className="slide-inner-background-content" style={bgStyling}>
              {slideLayout.id === "section-title-arch-image" && (
                <SectionTitleArchImage />
              )}

              {slideLayout.id === "one-column-arch-image" && (
                <OneColumArchImage />
              )}

              {(slideLayout.id === "one-column-half-circle-image" ||
                slideLayout.id === "campaign-info") && (
                <OneColumnHalfCircleImage />
              )}
              {slideLayout.id === "image-with-vertical-gradient-overlay" && (
                <ImageWithVerticalGradientOverlay />
              )}
              {(slideLayout.id === "one-column-arch-image" ||
                slideLayout.id === "one-column-half-circle-image" ||
                slideLayout.id === "campaign-info") && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <img
                    alt=""
                    style={{
                      minWidth: 683,
                      maxWidth: "100%",
                      height: "100%",
                      objectFit: "cover",
                      opacity: `${slideInnerBackgroundOpacity}`,
                      marginRight: `${
                        slideLayout.id === "one-column-arch-image" ? "3%" : ""
                      }`,
                    }}
                    src={backgroundImageURIGenerator(
                      slideInnerBackgroundImage,
                      false,
                      props.slide,
                      props.presentation.id,
                    )}
                  />
                </Box>
              )}
            </div>
          </div>

          {slideLayout.id === "advanced" ? (
            <>
              {iframeError ? (
                <div
                  className="content-wrapper w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: "#fff" }}
                >
                  <h6 style={{ color: "#888" }}>
                    Error loading slide. The slide may be missing or corrupt.
                  </h6>
                </div>
              ) : (
                <Iframe
                  id={props.slide.id}
                  slide={props.slide}
                  live={props.live}
                  presentation_id={props.presentation.id}
                  onTest={testIframe}
                />
              )}
            </>
          ) : (
            <div
              id={`slide-${props.slide.id}-editor`}
              className={innerContainerClasses()}
            />
          )}

          {slideLogo !== "" && (
            <div className={`logo-container ${slideLogoPosition}`}>
              <img
                src={`${themeDirectory}/dist/images/logos/${slideLogo}`}
                alt={`slide-logo`}
              />
            </div>
          )}

          {lockupEnabled() && slideLockup && (
            <div className={`lockup-container ${slideLockupPosition}`}>
              <img
                src={`${themeDirectory}/dist/images/lockups/${slideLockup}`}
                alt={`slide-lockup`}
              />
            </div>
          )}
        </div>

        {isSaving || isError ? (
          <div className="state-container">
            <Button className="state" disabled={true}>
              <span>
                {isSaving && !isError && (
                  <span className="saver">
                    <Loader width={20} height={20} color="#1d202b" />
                    <span className="label">Saving</span>
                  </span>
                )}
                {!isSaving && isError && (
                  <span className="error">
                    <WarningAmber fontSize={"small"} color={"error"} />
                    <span className="label">Error saving slide</span>
                  </span>
                )}
              </span>
            </Button>
          </div>
        ) : (
          <></>
        )}

        <Dialog
          className={`ss-modal settings-modal 
              ${props.showBackgroundSettings ? "background-modal" : ""} 
              ${props.showSlideSettings ? "slide-settings-modal" : ""} 
              ${props.showHelpDialog ? "slide-help-modal" : ""}`}
          open={showSettingsModal}
          maxWidth={
            props.showTransitionSettings ||
            props.showLayoutSettings ||
            props.showLogoSettings ||
            props.showHelpDialog
              ? "md"
              : props.showSlideSettings
                ? "lg"
                : props.showSlideSettings || props.showBackgroundSettings
                  ? "lg"
                  : "sm"
          }
          fullWidth={
            props.showTransitionSettings ||
            props.showSlideSettings ||
            props.showLogoSettings ||
            props.showBackgroundSettings ||
            props.showHelpDialog
              ? true
              : false
          }
          // fullScreen={props.showBackgroundSettings ? true : false}
          // transitionDuration={0}
          onClose={(event, reason) => {
            if (reason && reason === "backdropClick") return;
          }}
          PaperComponent={PaperComponent}
          hideBackdrop={transparentSettings}
          sx={
            transparentSettings
              ? {
                  "& .MuiPaper-root": {
                    backgroundColor: "transparent",
                    transition: "background-color .5s ease",
                    boxShadow: "none",
                  },
                }
              : {}
          }
        >
          <Box
            className={`ss-modal-title ${
              transparentSettings ? "transparent-opacity" : ""
            }`}
            id="ss-modal-title"
          >
            <Container fluid>
              <Row className="align-items-center justify-content-between">
                <Col xs={"auto"}>
                  <Typography variant="h3" owner="admin" color="adminText.dark">
                    {props.showHelpDialog ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: helpTextParser(
                            HelpText(
                              slideLayout.id,
                              hasImage,
                              slideContentPosition,
                              "header",
                            ),
                          ),
                        }}
                      />
                    ) : (
                      modalTitle()
                    )}
                  </Typography>
                </Col>
                <Col xs={"auto"}>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                  >
                    <Button
                      variant="outlined"
                      owner="admin"
                      dialogbtn="true"
                      dialogvariant="cancel"
                      onClick={() => {
                        updateSlideTheme(originalData.originalTheme);
                        setSlideLogo(originalData.originalSlideLogo);
                        setSlideLogoPosition(
                          originalData.originalSlideLogoPosition,
                        );
                        setSlideLockup(originalData.originalSlideLockup);
                        setSlideLockupPosition(
                          originalData.originalSlideLockupPosition,
                        );
                        setSlideLayout(originalData.originalSlideLayout);
                        setSlideContentPosition(
                          originalData.originalSlideContentPosition,
                        );
                        setSlideTransition(
                          originalData.originalSlideTransition,
                        );
                        setSlideTransitionSpeed(
                          originalData.originalSlideTransitionSpeed,
                        );
                        setSlideBackgroundColor(
                          originalData.originalSlideBackgroundColor,
                        );
                        setSlideBackgroundTransition(
                          originalData.originalSlideBackgroundTransition,
                        );
                        setSlideBackgroundImage(
                          originalData.originalSlideBackgroundImage,
                        );
                        setSlidePattern(originalData.originalSlidePattern);
                        setSlideBackgroundOpacity(
                          originalData.originalSlideBackgroundOpacity,
                        );
                        setSlideBackgroundRepeat(
                          originalData.originalSlideBackgroundRepeat,
                        );
                        setSlideBackgroundPosition(
                          originalData.originalSlideBackgroundPosition,
                        );
                        setSlideBackgroundSize(
                          originalData.originalSlideBackgroundSize,
                        );
                        setSlideInnerBackgroundColor(
                          originalData.originalSlideInnerBackgroundColor,
                        );
                        setSlideInnerBackgroundImage(
                          originalData.originalSlideInnerBackgroundImage,
                        );
                        setSlideInnerBackgroundOpacity(
                          originalData.originalSlideInnerBackgroundOpacity,
                        );
                        setSlideInnerBackgroundRepeat(
                          originalData.originalSlideInnerBackgroundRepeat,
                        );
                        setSlideInnerBackgroundPosition(
                          originalData.originalSlideInnerBackgroundPosition,
                        );
                        setSlideInnerBackgroundSize(
                          originalData.originalSlideInnerBackgroundSize,
                        );
                        setSlideAutoAnimate(
                          originalData.originalSlideAutoAnimate,
                        );
                        setSlideOverlayOpacity(
                          originalData.originalSlideOverlayOpacity,
                        );
                        setSlideOverlayWidth(
                          originalData.originalSlideOverlayWidth,
                        );
                        setSlideOverlayHeight(
                          originalData.originalSlideOverlayHeight,
                        );
                        setSlideContentWidth(
                          originalData.originalSlideContentWidth,
                        );
                        setSlideOverlayColor(
                          originalData.originalSlideOverlayColor,
                        );
                        setSlideOverlayRGB(
                          originalData.originalSlideOverlayRGB,
                        );
                        setSlideChapterMarker(
                          originalData.originalSlideChapterMarker,
                        );
                        setSlideDataTableColumn(
                          originalData.originalSlideDataTableColumn,
                        );
                        setArchContentAlignment(
                          originalData.originalArchCOntentAlignment,
                        );
                        setCapacityTags(originalData.originalCapacityTags);
                        setFragmentCount(originalData.originalFragmentCount);
                        setCancelled(true);
                        setSettingsModalShow(false);
                        props.onSettingsModalHide();
                      }}
                    >
                      <Typography variant="button" owner="admin" className="lh">
                        Cancel
                      </Typography>
                    </Button>
                    {!props.showHelpDialog && (
                      <Button
                        variant="contained"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="save"
                        className="lh"
                        onClick={() => {
                          setSettingsModalShow(false);
                          props.onSettingsModalHide();
                          updateSlide();
                        }}
                      >
                        <Typography
                          variant="button"
                          owner="admin"
                          className="lh"
                        >
                          Save
                        </Typography>
                      </Button>
                    )}
                  </Stack>
                </Col>
              </Row>
            </Container>
          </Box>
          <DialogContent
            sx={{
              pt: "20px !important",
              overflowX: "hidden",
            }}
            dividers={transparentSettings ? false : true}
          >
            <div
              className={`bg-settings-container ss-modal-content ${
                props.showHelpDialog ? "slide-help-modal" : ""
              }`}
            >
              {props.showBackgroundSettings && <>{backgroundSettings()}</>}
              {props.showLogoSettings && (
                <>
                  <div className={`settings-wrap-lg-main`}>
                    <LogoPicker
                      title={"Logo"}
                      slideLogo={slideLogo}
                      logos={theme_json?.logos}
                      themeDirectory={themeDirectory}
                      onLogoSelected={(slide_logo) => {
                        setSlideLogo(slide_logo);
                      }}
                    />
                  </div>
                  <Box>
                    <Row className="m-0 w-100 pt-3 px-0">
                      <Col className="px-0">
                        <Row>
                          <Col xs={"auto"} className="px-0">
                            <LogoPosition
                              slideLogoPosition={slideLogoPosition}
                              variant="standard"
                              size="small"
                              showTooltip={true}
                              logoPositions={theme_json?.logoPositions}
                              onLogoPosition={(position) => {
                                setSlideLogoPosition(position);
                              }}
                            />
                          </Col>
                          <Col xs={"auto"} className="ps-3">
                            <Alert severity="warning" sx={{ fontSize: 12 }}>
                              Please note, this option may be overridden via the
                              brand theme.
                            </Alert>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Box>
                  {lockupEnabled() && (
                    <>
                      <div className={`settings-wrap-lg-main mt-5`}>
                        <LogoPicker
                          title={client === "revo-h" ? "Footer" : "Lockup"}
                          isLockup={true}
                          slideLogo={slideLockup}
                          logos={theme_json?.lockups}
                          themeDirectory={themeDirectory}
                          onLogoSelected={(slide_lockup) => {
                            setSlideLockup(slide_lockup);
                          }}
                        />
                      </div>
                      {!theme_json.fixedLockup && (
                        <Box>
                          <Row className="m-0 w-100 pt-3 px-0">
                            <Col className="px-0">
                              <Row>
                                <Col xs={"auto"} className="px-0">
                                  <LogoPosition
                                    slideLogoPosition={slideLockupPosition}
                                    variant="standard"
                                    size="small"
                                    showTooltip={true}
                                    isLockup={true}
                                    onLogoPosition={(position) => {
                                      setSlideLockupPosition(position);
                                    }}
                                  />
                                </Col>
                                <Col xs={"auto"} className="ps-3">
                                  <Alert
                                    severity="warning"
                                    sx={{ fontSize: 12 }}
                                  >
                                    Please note, this option may be overridden
                                    via the brand theme.
                                  </Alert>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
              {props.showTransitionSettings && (
                <>
                  <Transition
                    title={"Slide transition"}
                    selectedTransition={slideTransition}
                    onTransitionSelected={(slide_transition) => {
                      setSlideTransition(slide_transition);
                      setTimeout(() => {
                        props.onRevealUpdate();
                      }, 10);
                    }}
                  />
                  <Transition
                    speedSlider={true}
                    title={"Background transition"}
                    selectedTransition={slideBackgroundTransition}
                    selectedTransitionSpeed={slideTransitionSpeed}
                    onTransitionSelected={(background_transition) => {
                      setSlideBackgroundTransition(background_transition);
                      setTimeout(() => {
                        props.onRevealUpdate();
                      }, 10);
                    }}
                    onTransitionSpeedSelected={(slide_transition_speed) => {
                      setSlideTransitionSpeed(slide_transition_speed);
                      setTimeout(() => {
                        props.onRevealUpdate();
                      }, 10);
                    }}
                  />
                </>
              )}
              {props.showLayoutSettings && (
                <div className={`settings-wrap-lg-main`}>
                  <LayoutPicker
                    layouts={theme_json?.layouts}
                    slideLayout={slideLayout}
                    slideContentPosition={slideContentPosition}
                    onLayoutSelected={(layout) => {
                      setSlideLayout(layout);
                    }}
                    onContentPositionSelected={(position) => {
                      setSlideContentPosition(position);
                    }}
                    categoryTitle={"Category"}
                    layoutTitle={"Layout"}
                  />
                </div>
              )}
              {props.showSlideSettings && <>{slideSettings()}</>}
              {props.showHelpDialog &&
                // <div
                //   dangerouslySetInnerHTML={{
                //     __html: helpTextParser(
                //       HelpText(
                //         slideLayout.id,
                //         hasImage,
                //         slideContentPosition,
                //         "body"
                //       )
                //     ),
                //   }}
                // />
                HelpTextParser(
                  HelpText(
                    slideLayout.id,
                    hasImage,
                    slideContentPosition,
                    "body",
                  ),
                )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          className={`
              ${
                props.showSlideAudioSettings
                  ? "audio-modal"
                  : "ss-modal settings-modal override-modal"
              } 
              ${
                props.showSlideNotes
                  ? "slide-notes-modal"
                  : "ps-modal presentation-settings-modal presentation-audio-modal"
              }`}
          open={showOverrideModal}
          maxWidth={"md"}
          fullWidth={true}
          onClose={(event, reason) => {
            if (reason && reason === "backdropClick") return;
          }}
          PaperComponent={PaperComponent}
        >
          <Box
            className={`${
              props.showSlideAudioSettings ? "ps-modal-title" : "ss-modal-title"
            } `}
            id="ss-modal-title"
          >
            <Container fluid>
              <Row className="align-items-center justify-content-between">
                <Col xs={"auto"}>
                  <Typography variant="h3" owner="admin" color="adminText.dark">
                    {modalTitle()}
                  </Typography>
                </Col>
                <Col xs={"auto"}>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                  >
                    <Button
                      variant="outlined"
                      owner="admin"
                      dialogbtn="true"
                      dialogvariant="cancel"
                      onClick={() => {
                        setSlideAudioData(originalData.originalSlideAudioData);
                        setSlideNotes(originalData.originalSlideNotes);
                        setShowOverrideMOdal(false);
                        props.onOverrideModalHide();
                      }}
                    >
                      <Typography variant="button" owner="admin" className="lh">
                        Cancel
                      </Typography>
                    </Button>
                    <Button
                      variant="contained"
                      owner="admin"
                      dialogbtn="true"
                      dialogvariant="save"
                      className="lh"
                      onClick={() => {
                        if (props.showSlideAudioSettings) {
                          props.onSlideAudioUpdate(
                            props.slide.id,
                            slideAudioData,
                          );
                        }
                        if (props.showSlideNotes) {
                          props.onSlideNotesUpdate(props.slide.id, slideNotes);
                        }
                        setShowOverrideMOdal(false);
                        props.onOverrideModalHide();
                      }}
                    >
                      <Typography variant="button" owner="admin" className="lh">
                        Save
                      </Typography>
                    </Button>
                  </Stack>
                </Col>
              </Row>
            </Container>
          </Box>
          <DialogContent
            sx={
              props.showSlideNotes
                ? {
                    pt: "20px !important",
                    overflowX: "hidden",
                  }
                : {}
            }
            dividers={true}
          >
            {props.showSlideAudioSettings && (
              <Container fluid>
                <AddAudio
                  user={user}
                  audioData={slideAudioData}
                  slideSetting={true}
                  onAudioChange={(audio) => {
                    setSlideAudioData(audio);
                  }}
                />
              </Container>
            )}
            {props.showSlideNotes && (
              <div
                className={`bg-settings-container ss-modal-content ${
                  props.slideNotes ? "slide-notes-modal" : ""
                }`}
              >
                {slideNotesInput()}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <MediaLibrary
          user={user}
          csrf_token={props.csrf_token}
          presentation={props.presentation}
          slide={props.slide}
          slideLayout={slideLayout}
          mediaType={currentBlock?.bundle ? currentBlock.bundle : mediaType}
          json={json}
          files={slideFiles}
          setMediaType={(type) => {
            setMediaType(type);
          }}
          setShowMediaLibrary={(condition) => {
            setShowMediaLibrary(condition);
            setSlideAudio(condition);
            setSlideFiles({});
          }}
          showMediaLibrary={showMediaLibrary}
          onMediaInsert={(media) => {
            if (slideAudio) {
              setShowMediaLibrary(false);
              setSlideAudio(false);
              setMediaType("all");
              setSlideAudioData({
                src: media.field_media_audio,
                autoPlay: false,
                name: media.name,
                mid: media.mid,
              });
            } else {
              if (currentBlock?.details) {
                let data = currentBlock?.data;
                let newData = { ...data };

                if (currentBlock?.bundle === "image") {
                  if (currentBlock?.details.name === "gallery") {
                    const imageData = {
                      mid: media.mid,
                      url: media.field_media_image,
                      caption: media.alt,
                      x: media.x,
                      y: media.y,
                      width: media.width,
                      height: media.height,
                      maxWidth: media.maxWidth,
                      maxHeight: media.maxHeight,
                      listingId: media?.listingId,
                      listingName: media?.listingName,
                      assetId: media?.assetId,
                      fileId: media?.fileId,
                      iceMedia: media?.iceMedia,
                    };

                    if (Array.isArray(data.images)) {
                      newData.images.push(imageData);
                    } else {
                      newData.images = [];
                      newData.images.push(imageData);
                    }
                  } else if (
                    currentBlock?.details.name === "imageTextOverlay"
                  ) {
                    newData.images[currentBlock?.imageIndex].mid = media.mid;
                    newData.images[currentBlock?.imageIndex].url =
                      media.field_media_image;
                    newData.images[currentBlock?.imageIndex].x = media.x;
                    newData.images[currentBlock?.imageIndex].y = media.y;
                    newData.images[currentBlock?.imageIndex].width =
                      media.width;
                    newData.images[currentBlock?.imageIndex].height =
                      media.height;
                    newData.images[currentBlock?.imageIndex].maxWidth =
                      media.maxWidth;
                    newData.images[currentBlock?.imageIndex].maxHeight =
                      media.maxHeight;
                    newData.images[currentBlock?.imageIndex].listingId =
                      media.listingId;
                    newData.images[currentBlock?.imageIndex].listingName =
                      media.listingName;
                    newData.images[currentBlock?.imageIndex].assetId =
                      media.assetId;
                    newData.images[currentBlock?.imageIndex].fileId =
                      media.fileId;
                    newData.images[currentBlock?.imageIndex].iceMedia =
                      media.iceMedia;
                  } else if (
                    currentBlock?.details.name === "atAGlance" &&
                    currentBlock?.imageSlot
                  ) {
                    if (!newData.imageOverrides) newData.imageOverrides = {};
                    newData.imageOverrides[currentBlock.imageSlot] =
                      media.field_media_image;
                  } else {
                    newData.mid = media.mid;
                    newData.url = media.field_media_image;
                    newData.x = media.x;
                    newData.y = media.y;
                    newData.width = media.width;
                    newData.height = media.height;
                    newData.maxWidth = media.maxWidth;
                    newData.maxHeight = media.maxHeight;
                    newData.listingId = media.listingId;
                    newData.listingName = media.listingName;
                    newData.assetId = media.assetId;
                    newData.fileId = media.fileId;
                    newData.iceMedia = media.iceMedia;
                  }
                }

                if (currentBlock?.bundle === "video") {
                  newData.mid = media.mid;
                  newData.url = media.field_media_video;
                  newData.timestamp = media.timestamp;
                  newData.poster = `${filesystem_root}/${subdomain}/videos/${media.mid}.jpg`;
                  newData.listingId = media.listingId;
                  newData.listingName = media.listingName;
                  newData.assetId = media.assetId;
                  newData.fileId = media.fileId;
                  newData.iceMedia = media.iceMedia;

                  if (media.external) {
                    newData.external = true;
                    newData.url = media.url;
                    newData.poster = media.poster;
                  }

                  if (media.remote) {
                    newData.remote = true;
                    newData.poster = media.poster;
                    newData.url = media.url;
                  }
                }

                if (currentBlock?.bundle === "audio") {
                  newData.mid = media.mid;
                  newData.url = media.field_media_audio;
                }

                safeUpdateBlock(
                  editorInstance.current,
                  currentBlock?.details?.id,
                  newData,
                );
              }
            }
          }}
          selectedMid={currentBlock?.data?.mid}
          selectedListingId={currentBlock?.data?.listingId}
          selectedAssetId={currentBlock?.data?.assetId}
          selectedFileId={currentBlock?.data?.fileId}
          selectedIceMedia={currentBlock?.data?.iceMedia}
          currentBlock={currentBlock}
        />

        <IconSelector
          icons={getLayoutIcons(slideLayout.id, props.slide.theme.label)}
          iconColors={getColors(0, true)}
          iconColorsSecondary={getColors(1, true)}
          setShowIconSelector={(condition) => {
            setShowIconSelector(condition);
          }}
          currentBlock={currentBlock}
          showIconSelector={showIconSelector}
          onIconSelected={(icon, color) => {
            if (currentBlock?.details) {
              let data = currentBlock?.data;
              let newData = { ...data };

              if (icon) {
                if (icon.icon) {
                  newData.icon = icon.icon;
                }

                if (icon.name) {
                  newData.name = icon.name;
                }
              }

              if (color) {
                newData.color = color;
              }

              safeUpdateBlock(
                editorInstance.current,
                currentBlock?.details?.id,
                newData,
              );
            }
          }}
        />
      </section>
    </>
  );
}

const Iframe = (props) => {
  const isLibrary =
    parseInt(props.slide.initial_presentation) !==
    parseInt(props.presentation_id)
      ? true
      : false;
  let src = `${proxy}${props.slide.slide_html_path}`;

  if (isLibrary) {
    src = `${proxy}/${subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/${props.id}/${props.id}.html`;

    if (props.slide.field_migration_id) {
      src = `${proxy}/${subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/${props.id}.html`;
    }
  } else {
    if (props.live) {
      src = `${proxy}/${subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${props.id}/${props.id}.html`;
      if (props.slide.field_migration_id) {
        src = `${proxy}/${subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${props.id}.html`;
      }
    }
  }

  props.onTest(src);

  return useMemo(() => {
    return (
      <iframe
        id={`frame-${props.id}`}
        className="advanced-slide"
        title={`Advanced Slide ${props.id}`}
        data-src={src}
      />
    );
  }, [props.id, src]);
};
